import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getBoldStrokeWidth, validateText} from '@PosterWhiteboard/libraries/text.library';
import type {Point} from '@Utils/math.util';
import {degreesToRadians} from '@Utils/math.util';
import {assertNever} from '@Utils/assert-never.util';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHex, rgbaToHexString, rgbaToRgbaString} from '@Utils/color.util';
import {noop} from '@Utils/general.util';
import {toWellFormed} from '@Utils/string.util';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import {cloneLineStylesRecord, scaleRunFontSizes, TextStyleRuns} from '@PosterWhiteboard/items/text-item/text-style-runs.class';
import type {TextLineListObject, TextLineStylesObject, TextRunStylesObject, TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import {getUniformLineList} from '@PosterWhiteboard/items/text-item/text-line-list.library';
import {FONT_SIZE_MAX, FONT_SIZE_MIN} from '@PosterWhiteboard/items/text-item/text-item.types';
import {isOutlineShapeType, TextShape} from '@PosterWhiteboard/items/text-item/text-shape.class';
import {TextShapeGuideStyle, TextShapeType} from '@PosterWhiteboard/items/text-item/text-shape.types';
import {isTextOnPathPlaceholder} from '@PosterWhiteboard/items/text-item/text-on-path-placeholder.library';
import {getCurveRadius, getCurveSeamGap, getOutlineRoomInBox, getPathTotalLength, getTextShapeOutline, getTextShapePathData, getTextWaveWarp} from '@PosterWhiteboard/items/text-item/text-shape-path.library';
import type {TextShapeBounds, TextShapeOutlineFitBox} from '@PosterWhiteboard/items/text-item/text-shape-path.library';
import {getRichTextPdfRasterizeReason} from '@PosterWhiteboard/items/text-item/text-item-pdf-safety.library';
import type {TextPdfRasterizeReason} from '@PosterWhiteboard/items/text-item/text-item-pdf-safety.library';
import {applyAlignmentToTextboxSelection, applyLineStylesToFabricTextbox, applyListToTextboxSelection, applyPerGraphemeRunStylesToTextboxSelection, applyRunStylesToTextboxSelection, applyStyleRunsToFabricTextbox, ensureSelectionApplyFontsAreLoaded, getEffectiveFontSizesForSelection, getGraphemeCount, getLineStylesClampedToTextbox, getTextSelectionStylesSummary, harvestLineStylesFromFabricTextbox, harvestStyleRunsFromFabricTextbox, refreshLineListMarkersOnTextbox, remeasureTextboxForLoadedFonts, replaceTextboxTextPreservingStyles, withExistingIndent} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';
import {DebouncedTextCommit, TEXT_COMMIT_DEBOUNCE_MS} from '@PosterWhiteboard/items/text-item/debounced-text-commit';
import {insertPastedTextIntoTextbox, resolvePastedText} from '@PosterWhiteboard/items/text-item/pasted-text.library';
import {TypedListPrefixConversion} from '@PosterWhiteboard/items/text-item/typed-list-prefix.library';
import {PendingTypingStyles} from '@PosterWhiteboard/items/text-item/pending-typing-styles.class';
import cloneDeep from 'lodash/cloneDeep';
import {getIsSpellCheckEnabledFromStore} from '@Libraries/spell-check-library';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {TextStylesObject} from '@PosterWhiteboard/classes/text-styles.class';
import {
  DEFAULT_STROKE_WIDTH,
  TEXT_OUTLINE_STROKE_WIDTH_FACTOR,
  TextHorizontalAlignType,
  TextStyles,
  TextVerticalAlignType,
} from '@PosterWhiteboard/classes/text-styles.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {FillSurface, withLayoutForSurface} from '@PosterWhiteboard/classes/fill-surface';
import {closeTextSelectionPopup, handleTextSelectionPopupMenu, shouldShowTextSelectionPopup} from '@Libraries/text-selection-library';
import {fontSupportsText} from '@Utils/font.util';
import {ElementDataType} from '@Libraries/add-media-library';
import type {ItemCoordinates} from '@PosterWhiteboard/page/add-item.class';
import type {FabricObject, ObjectEvents, SerializedShadowOptions, TFiller, TPointerEvent} from '@postermywall/fabricjs-2';
import {Canvas, FixedLayout, Group, LayoutManager, Path, Point as FabricPoint, Rect, Textbox, util} from '@postermywall/fabricjs-2';
import {TextBackground} from '@PosterWhiteboard/items/text-item/text-background';
import type {DeepPartial} from '@/global';
import {placeEditSessionCaret} from '@PosterWhiteboard/items/text-item/text-edit-caret.library';
import type {ClonePositionalParams, EnterEditModeOpts, TextShapedEditSession, TextShapeSliderDrag, TextShapeWriteInFlight} from '@PosterWhiteboard/items/text-item/text-item.types';
import {addFonts, BULLETS_FONT_FAMILY, getFontFamilyNameForVariations, isBoldVariationAvaliableForFont, isFontLoaded, loadBulletsFont} from '@/libraries/font-library';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {NUM_FRACTION_DIGITS} from '@PosterWhiteboard/common.types';
import round from 'lodash/round';
import clamp from 'lodash/clamp';
import {hideCustomControls, unhideCustomControls} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {getTextBoxBorderWidth} from '@PosterWhiteboard/items/text-item/text-box-border.library';

import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {isTextEntryElementFocused} from '@Utils/dom.util';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const DEFAULT_FONT_SIZE_FRACTION = 0.18;
export const MAX_CHARACTERS_LIMIT = '10000';
const PDF_UNREGISTRABLE_FONT_FAMILIES = ['SANTORegular', 'BarrioRegular'];
const PDF_UNSAFE_RTL_SCRIPTS = ['arabic', 'hebrew'];

export interface FabricItemDimensions {
  width: number;
  height: number;
}

interface ModelCoordinates {
  x?: number;
  y?: number;
}


/**
 * A width large enough that the fabric textbox never soft-wraps while measuring the natural
 * (unwrapped) width of the text. Used by {@link TextItem.updateTextWithoutWrapping}.
 */
const UNWRAPPED_TEXT_MEASURE_WIDTH = 100000;

/**
 * Extra slack (as a fraction of font size) added to the measured text width when fitting the box
 * to unwrapped text. Fabric's line-wrapping comparison accumulates width slightly differently than
 * {@link Textbox.calcTextWidth}, so an exact fit can tip the in-progress word onto a new line until
 * the next character (e.g. a space) is typed. The buffer keeps a word from wrapping while it is
 * still being typed.
 */
const UNWRAPPED_TEXT_WIDTH_BUFFER_FACTOR = 0.5;

/**
 * How far a glyph sitting on a shape path reaches above and below it, in em of its own size. The
 * path is the BASELINE (fabric's default for text on a path): underline and strikethrough are
 * drawn from the baseline, so any other alignment puts them through or over the glyphs. Ascenders
 * and capitals reach about 0.8 em up, descenders about 0.25 em down, with a little room for faces
 * whose ink leaves the em box.
 */
const SHAPED_GLYPH_ASCENT_EM = 0.85;
const SHAPED_GLYPH_DESCENT_EM = 0.3;
/** Room kept between the shaped glyphs' reach and the box edge, in text units. */
const SHAPED_BOX_MARGIN = 2;
/** The opacity of the shaped preview drawn under the text being typed in a shaped item's edit session. */
const SHAPED_EDIT_PREVIEW_OPACITY = 0.3;
/** The preview sits between the background rect (0) and the editing textbox in the group. */
const SHAPED_EDIT_PREVIEW_GROUP_INDEX = 1;
/** The font never fits below this: a paragraph forced into a small box stays readable and tells the user to grow the box. */
const SHAPED_FIT_FONT_SIZE_MIN = 8;
/** A fit this close to 1 leaves the font alone, so a load does not drift the size by rounding. */
const SHAPED_FIT_TOLERANCE = 0.005;
/** The fit is linear but for the fixed margins and stroke; a second pass or two lands within the tolerance. */
const SHAPED_FIT_MAX_PASSES = 3;
/** Halving the font range this many times lands the font a figure's text comes back up to within a thousandth of a pixel of its room. */
const OUTLINE_REGROW_BISECTION_STEPS = 24;
/** The guide a shape's path shows under the text while the item is selected: a thin line in the selection blue, in the item's units. */
const SHAPE_GUIDE_COLOR = '#3fbce8';
const SHAPE_GUIDE_WIDTH_EM = 0.04;
/** A drawn path's guide is dashed, so it reads as the room left for text rather than part of the design. */
const DRAWN_PATH_GUIDE_DASH_EM = [0.3, 0.2];
/** The curve's circle guide is a hairline on screen, however far the canvas is zoomed. */
const CURVE_CIRCLE_GUIDE_SCREEN_WIDTH = 1.5;
/** A single letter has no curve to show, so the circle guide waits for a second one (the design's "no guide for one letter"). */
const CURVE_CIRCLE_GUIDE_MIN_LETTERS = 2;
/** Text this close to a drawn path's full length counts as filling it, so the guide does not flicker on at the fit's rounding. */
const DRAWN_PATH_GUIDE_SLACK = 1;
/** A path longer than the text by less than this, in the item's units, is the arc's rounding, not room to spread the letters into. */
const SHAPED_SPREAD_MIN_SLACK = 0.5;
/** A layout past the box by less than this, in the item's units, is the glyph boxes' rounding where they turn round a figure, not a layout the box must grow for. */
const SHAPED_BOX_OVERRUN_TOLERANCE = 0.5;

/** A box dimension grown to hold a layout that overruns it by more than the rounding tolerance, else left as it is. */
const getSizeGrownToHold = (current: number, needed: number): number => {
  return needed > current + SHAPED_BOX_OVERRUN_TOLERANCE ? needed : current;
};

/**
 * saves the list style from last pasted text
 */

export class TextItem extends Item {
  declare _fabricObject: Group;

  public gitype = ITEM_TYPE.TEXT;
  public text = '';
  public verticalAlign: TextVerticalAlignType = TextVerticalAlignType.TOP;
  public verticalPadding = 0;
  /**
   * Per-side inset between the text and the item's bounds, in the item's local space — it scales
   * with the item exactly like the text does (no /scale compensation). Legacy posters are migrated
   * from the old constant on-canvas 11px inset by back-computing `11 / scale` (MigrateToVersion68).
   */
  public padding = 0;
  public background: TextBackground;
  public backgroundFabricObject!: Rect;
  public fabricTextbox!: Textbox;
  public textStyles: TextStyles;
  public styleRuns = new TextStyleRuns();
  public lineStyles: Record<string, TextLineStylesObject> = {};
  public shape = new TextShape();
  /**
   * The font size the user set before a figure's fit shrank the text to its outline, so the text
   * comes back up to it as it gets shorter (the design's "and vice versa"); null while no fit has
   * shrunk it. Editor state only, not poster data: it lives as long as the item does in this
   * editor, and a reload or an undo that writes the font starts afresh from the font it finds.
   */
  private fontSizeBeforeShapeFit: number | null = null;
  public enterEditModeOnMouseUp = true;
  private hiddenTextareaPasteListener: ((event: Event) => void) | null = null;
  private hiddenTextareaInputListener: (() => void) | null = null;
  private hiddenTextareaKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  private hiddenTextareaBeforeInputListener: ((event: Event) => void) | null = null;

  /**
   * Effective per-grapheme font sizes captured when a relative size drag starts, so every tick
   * scales from the original sizes instead of compounding off the previous tick. Cleared on the
   * drag's undoable end-commit.
   */
  /*
   * The private fields below are soft-`private`, not `#`: duplicating an item runs the live
   * instance through lodash cloneDeep (active-selection.class duplicateSelection), which rebuilds
   * from the prototype without running the constructor — a clone therefore has NO `#` slots, and
   * any later `#` read on it throws a brand-check TypeError (the storedRuns lesson in
   * text-style-runs.class.ts). `#` methods are brand-checked the same way, so every method
   * {@link toObject} can reach on a duplicate is soft-`private` too.
   */
  private selectionFontSizeScaleBaseline: {selectionStart: number; selectionEnd: number; effectiveFontSizes: number[]} | null = null;
  /**
   * The base width while the fabric textbox is not its source of truth: before the textbox
   * exists (item init / load), and while a shape path drives the textbox's width (the stored
   * base width is what the box returns to when the shape comes off). Otherwise {@link baseWidth}
   * reads and writes the textbox itself.
   */
  private detachedBaseWidth = 0;

  /**
   * The wrap width at edit-session start. Fabric grows the edited textbox's width when a long
   * word exceeds it; resetting to this width on every change lets the box shrink back once that
   * word is deleted, exactly like the pre-session width behaves between sessions.
   */
  private editSessionBaseWidth: number | null = null;
  private editSessionCacheRefreshDisposer: (() => void) | null = null;
  /** Re-entrancy guard for the shaped layout, which itself sets layout properties fabric would relayout on. */
  private isLayingOutShape = false;
  /**
   * A shaped item's edit session: the faded textbox laid out on the shape under the text being
   * typed, refreshed on every change so the user sees what the typing will look like, as the
   * requirement asks (and Canva does), and the box the session keeps to. Null outside a session
   * and for an unshaped item.
   */
  private shapedEditSession: TextShapedEditSession | null = null;
  /** The shape write going through {@link updateFromObject}, for the layout it triggers; null outside one. */
  private shapeWriteInFlight: TextShapeWriteInFlight | null = null;
  /**
   * The box as a shape slider drag found it, from the drag's first move to its release: a figure
   * fills its box and the box follows the figure, so without this a ring the drag grew could
   * never shrink back when the slider came up again. Null outside a drag.
   */
  private shapeSliderDrag: TextShapeSliderDrag | null = null;
  /**
   * Where the path's origin sat on the page when an edit session unshaped a box sized by its path
   * (a drawn path, a spiral): the session's preview is placed by it, and the reshape after the
   * session puts the path back on it.
   */
  private pathOriginBeforeEditSession: Point | null = null;
  /** The canvas render listener drawing a curve's circle guide while its slider is dragged; null the rest of the time. */
  private curveCircleGuideListener: ((event: {ctx: CanvasRenderingContext2D}) => void) | null = null;
  /** Disposers for the fabric listeners attached to the textbox for one edit session. */
  private editSessionListenerDisposers: VoidFunction[] = [];

  private readonly textCommit = new DebouncedTextCommit();

  /** Styles buffered at a collapsed caret for the next characters typed — see the class doc. */
  private readonly pendingTypingStyles = new PendingTypingStyles();
  /** Converts a typed "- ", "* " or "1. " into a list row — see the class doc of the conversion. */
  private readonly typedListPrefixConversion = new TypedListPrefixConversion();

  public get baseWidth(): number {
    return this.isTextboxTheBaseWidthSource() ? this.fabricTextbox.width : this.detachedBaseWidth;
  }

  /**
   * Writes the textbox width without fabric's layout: a write arrives with the model's new text
   * still to come (copyVals runs before the textbox is laid out), and laying out now would size
   * the box for the OLD text — undoing a typed long word restored the old baseWidth, fabric
   * re-grew it for the word still on the textbox, and the short text then kept that width. The
   * layout that follows every update sets text and width together.
   */
  public set baseWidth(width: number) {
    if (this.isTextboxTheBaseWidthSource()) {
      this.fabricTextbox.width = width;
    } else {
      this.detachedBaseWidth = width;
    }
  }

  /**
   * Soft-private, not `#`: {@link toObject} reads {@link baseWidth} on a cloneDeep duplicate of the
   * live item, and a `#` method brand-check throws there (see the class's field note above).
   */
  private isTextboxTheBaseWidthSource(): boolean {
    return this.fabricTextbox !== undefined && !this.fabricTextbox.path && this.shapedEditSession === null;
  }

  /** True when the glyphs follow a shape path instead of filling the wrapped box. */
  public isShaped(): boolean {
    return this.shape.isShaped();
  }

  /**
   * The textbox currently in fabric's text-editing mode, or null outside an edit session.
   * Editing happens in place (fabric edits inside interactive groups), so while editing this is
   * always {@link fabricTextbox}.
   */
  public getEditingTextbox(): Textbox | null {
    return this.fabricTextbox?.isEditing ? this.fabricTextbox : null;
  }

  constructor(page: Page) {
    super(page);
    this.textStyles = new TextStyles();
    this.background = new TextBackground(this);
  }

  override get fabricObject(): Group {
    return this._fabricObject;
  }

  public override async updateFromObject(
    textItemObject: DeepPartial<TextItemObject>,
    {
      updateRedux = true,
      applyLoadTimeNormalizations = false,
      undoable = true,
      fireModifiedEvent = true,
      doInvalidate = true,
      replayPosterOnUpdateDone = false,
      checkForDurationUpdate = false,
    }: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // Commit any debounced typing first so this update applies on top of the edited textbox's
    // current text instead of overwriting it with a stale model (and so the pending timer can
    // never fire later and clobber this update).
    await this.flushPendingTextCommit();
    // Captured before the update applies — see Item.updateFromObject.
    const pageWasVideoBeforeUpdate = checkForDurationUpdate ? this.page.isVideo() : true;
    const isInTextEditMode = !!this.getEditingTextbox();
    const anchorToKeep = this.#keepsAnchorThroughUpdate(textItemObject, applyLoadTimeNormalizations) ? this.#getAnchorOnPage() : null;
    const boxSizeForFontSize = this.#getBoxSizeForFontSizeWrite(textItemObject, applyLoadTimeNormalizations);
    const pathOriginToKeep = this.#getPathOriginToKeep(textItemObject, applyLoadTimeNormalizations, isInTextEditMode);
    this.shapeWriteInFlight = this.#getShapeWriteInFlight(textItemObject, applyLoadTimeNormalizations);
    if (this.shapeWriteInFlight !== null && !undoable) {
      this.shapeSliderDrag ??= {box: {width: this.fabricObject.width, height: this.fabricObject.height}, hugging: this.shapeWriteInFlight.hugging};
    }
    try {
      this.#forgetFontSizeBeforeShapeFit(textItemObject);
      this.copyVals(textItemObject);
      if (boxSizeForFontSize !== null) {
        this.fabricObject.set(boxSizeForFontSize);
      }
      await this.updateFabricObjectFromObject(textItemObject);
      const fontsStillLoading = this.getFonts(true).filter((fontFamily) => !isFontLoaded(fontFamily));
      await this.ensureFontsAreLoaded();
      this.#remeasureForLoadedFonts(fontsStillLoading);

      if (applyLoadTimeNormalizations) {
        this.text = validateText(this.text);
        if (this.#lacksStoredBaseWidth(textItemObject)) {
          this.updateBaseWidth();
        }
      }

      if (doInvalidate) {
        await this.invalidate();
      }
    } finally {
      if (this.shapeWriteInFlight === null || undoable) {
        this.shapeSliderDrag = null;
      }
      this.shapeWriteInFlight = null;
    }
    if (applyLoadTimeNormalizations) {
      // A load's fit lands the text in the box it was saved with; the font it came down from is
      // not one the user set in this editor, so nothing brings the text back up to it on the
      // next touch (a drag regrew a loaded heart's text twice, 2026-10-29).
      this.fontSizeBeforeShapeFit = null;
    }
    // Only after the invalidate: its layout is what resizes the box (round the path for a
    // path-sized box, from the top-left corner for any other), so the anchor goes back once the
    // box has its new size.
    if (anchorToKeep !== null) {
      this.#moveAnchorTo(anchorToKeep);
    }
    if (pathOriginToKeep !== null) {
      this.#movePathOriginTo(pathOriginToKeep);
    }
    if (!isInTextEditMode) {
      this.pathOriginBeforeEditSession = null;
    }

    // Mirrors Item.updateFromObject: a span change without a clock tick must re-gate visibility.
    this.updateItemVisibility();

    if (checkForDurationUpdate) {
      this.checkItemForPageDurationUpdate(pageWasVideoBeforeUpdate);
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    // Restore typing focus to the canvas after click-type contextual options (e.g. Bold), but
    // never steal it from a field the user is typing in (e.g. the font size input), and only on
    // undoable commits — refocusing per slider tick (undoable: false) flickers the caret.
    const editingTextbox = this.getEditingTextbox();
    if (isInTextEditMode && undoable && editingTextbox && editingTextbox.hiddenTextarea !== document.activeElement && !isTextEntryElementFocused() && !isEditorMobileVariant()) {
      editingTextbox.hiddenTextarea?.focus({preventScroll: true});
    }
    if (editingTextbox) {
      this.#publishTextSelectionStyles();
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
    if (replayPosterOnUpdateDone) {
      await this.page.poster.replayPage(this.page.hashedID);
    }
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const styles: CopyableItemStyles = {
      ...super.getCopyableStyles(),
      scaleX: this.fabricObject.scaleX,
      scaleY: this.fabricObject.scaleY,
      textStyles: this.textStyles.toObject(),
      frontFill: this.textStyles.fill.toObject(),
      backFill: this.background.fill.toObject(),
      verticalAlign: this.verticalAlign,
      verticalPadding: this.verticalPadding,
      styleRuns: this.styleRuns.toObject(),
      lineStyles: cloneDeep(this.lineStyles),
      shape: this.shape.toObject(),
      textBorder: {
        solidBorderColor: this.textStyles.strokeColor,
        solidBorderType: this.textStyles.stroke ? BorderType.RECTANGLE_BORDER : BorderType.NONE,
        solidBorderThickness: this.textStyles.strokeWidth * 100,
      },
    };

    if (this.background.fill.fillType !== FillTypes.NONE) {
      styles.rx = this.background.rx;
      styles.ry = this.background.ry;
    }

    return styles;
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<TextItemObject> = {};
    if (copiedStyles.textStyles !== undefined) {
      stylesObj.textStyles = copiedStyles.textStyles;
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.background = {
        fill: withLayoutForSurface(copiedStyles.backFill, FillSurface.TEXT_BACKGROUND),
      };
    }
    if (copiedStyles.verticalAlign !== undefined) {
      stylesObj.verticalAlign = copiedStyles.verticalAlign;
    }
    if (copiedStyles.verticalPadding !== undefined) {
      stylesObj.verticalPadding = copiedStyles.verticalPadding;
    }
    if (copiedStyles.styleRuns !== undefined) {
      stylesObj.styleRuns = this.#getStyleRunsClampedToOwnText(copiedStyles.styleRuns);
    }
    if (copiedStyles.lineStyles !== undefined) {
      stylesObj.lineStyles = getLineStylesClampedToTextbox(copiedStyles.lineStyles, this.fabricTextbox);
    }
    if (copiedStyles.shape !== undefined) {
      stylesObj.shape = copiedStyles.shape;
    }
    if (copiedStyles.rx !== undefined) {
      stylesObj.background = {
        ...stylesObj.background,
        rx: copiedStyles.rx,
        ry: copiedStyles.ry,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.textStyles = {
        ...stylesObj.textStyles,
        stroke: copiedStyles.textBorder.solidBorderType !== BorderType.NONE,
        strokeWidth: copiedStyles.textBorder.solidBorderThickness / 100,
        strokeColor: copiedStyles.textBorder.solidBorderColor,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.textStyles = {
        ...stylesObj.textStyles,
        fill: withLayoutForSurface(copiedStyles.frontFill, FillSurface.TEXT),
      };
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['border']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override copyVals(obj: DeepPartial<TextItemObject>): void {
    const {textStyles, text, background, styleRuns, lineStyles, shape, ...itemObj} = obj;
    super.copyVals(itemObj);
    if (shape !== undefined) {
      this.shape.copyVals(shape);
    }
    if (text !== undefined) {
      this.text = toWellFormed(text);
    }
    if (background) {
      this.background.copyVals(background);
    }
    if (styleRuns !== undefined) {
      this.styleRuns.replaceRuns(styleRuns);
    }
    if (lineStyles !== undefined) {
      this.lineStyles = cloneLineStylesRecord(lineStyles);
    }
    if (textStyles !== undefined && styleRuns === undefined && this.styleRuns.hasRuns()) {
      this.#reconcileRunsWithBaseChange(textStyles);
    }
    if (textStyles?.textAlign !== undefined && lineStyles === undefined && Object.keys(this.lineStyles).length > 0) {
      this.#clearLineAlignmentOverrides();
    }
    if (itemObj.shadow !== undefined && styleRuns === undefined && this.styleRuns.hasRuns()) {
      this.#clearRunShadowOverrides();
    }
    this.textStyles.copyVals(textStyles);
  }

  /**
   * A whole-box shadow write (set or remove) is an explicit "shadow it all": the item shadow
   * composites over the whole group, so surviving per-run shadow overrides could neither show
   * nor be cleared — they go with it.
   */
  #clearRunShadowOverrides(): void {
    const clearedRuns = this.styleRuns.toObject().map((run) => ({...run, styles: {...run.styles, shadow: null}}));
    this.styleRuns.replaceRuns(clearedRuns);
  }

  /**
   * A whole-box alignment write is an explicit "align it all" — per-line overrides would keep it
   * from visibly applying, so they clear; other per-line keys survive. Entries left with nothing
   * but nulls drop entirely.
   */
  #clearLineAlignmentOverrides(): void {
    const clearedLineStyles: Record<string, TextLineStylesObject> = {};
    for (const [lineIndex, lineStyleObject] of Object.entries(this.lineStyles)) {
      const cleared: TextLineStylesObject = {...lineStyleObject, textAlign: null};
      if (Object.values(cleared).some((value) => value !== null)) {
        clearedLineStyles[lineIndex] = cleared;
      }
    }
    this.lineStyles = clearedLineStyles;
  }

  /**
   * Keeps whole-box writes meaningful over mixed (run-styled) content: a whole-box font size
   * scales every run's size proportionally so the contrast between runs survives; any other
   * whole-box write becomes the new base AND clears that property's run overrides — a whole-box
   * write is an explicit "make it all X", so it clears even when the value already equals the
   * base. The one recurring equal-value echo (the debounced text commit's script/font detection)
   * is filtered out at its source in {@link updateText}.
   */
  #reconcileRunsWithBaseChange(incomingTextStyles: DeepPartial<TextStylesObject>): void {
    const scaleRunFontSizesBy = incomingTextStyles.fontSize !== undefined && incomingTextStyles.fontSize !== this.textStyles.fontSize && this.textStyles.fontSize > 0 ? incomingTextStyles.fontSize / this.textStyles.fontSize : null;
    const clearFontFamily = incomingTextStyles.fontFamily !== undefined;
    const clearIsBold = incomingTextStyles.isBold !== undefined;
    const clearIsItalic = incomingTextStyles.isItalic !== undefined;
    const clearUnderLine = incomingTextStyles.underLine !== undefined;
    const clearLineThrough = incomingTextStyles.lineThrough !== undefined;
    const clearFillColor = incomingTextStyles.fill !== undefined;
    const clearOutline = incomingTextStyles.stroke !== undefined || incomingTextStyles.strokeColor !== undefined || incomingTextStyles.strokeWidth !== undefined;

    const reconciledRuns = this.styleRuns.toObject().map((run) => ({
      ...run,
      styles: {
        ...run.styles,
        fontSize: scaleRunFontSizesBy !== null && run.styles.fontSize !== null ? run.styles.fontSize * scaleRunFontSizesBy : run.styles.fontSize,
        fontFamily: clearFontFamily ? null : run.styles.fontFamily,
        fontLicense: clearFontFamily ? null : run.styles.fontLicense,
        isBold: clearIsBold ? null : run.styles.isBold,
        isItalic: clearIsItalic ? null : run.styles.isItalic,
        underLine: clearUnderLine ? null : run.styles.underLine,
        lineThrough: clearLineThrough ? null : run.styles.lineThrough,
        fillColor: clearFillColor ? null : run.styles.fillColor,
        fillGradient: clearFillColor ? null : run.styles.fillGradient,
        outline: clearOutline ? null : run.styles.outline,
      },
    }));
    this.styleRuns.replaceRuns(reconciledRuns);
  }

  private async ensureFontsAreLoaded(): Promise<void> {
    await this.checkForFontsLoad();
    await this.checkForBulletsFontLoad();
  }

  /**
   * Pasted styles keep the source item's run indices; anything beyond this item's own text is
   * trimmed away.
   */
  #getStyleRunsClampedToOwnText(styleRuns: TextStyleRunObject[]): TextStyleRunObject[] {
    const clampedRuns = new TextStyleRuns();
    clampedRuns.replaceRuns(styleRuns);
    clampedRuns.clampToGraphemeCount(getGraphemeCount(this.fabricTextbox));
    return clampedRuns.toObject();
  }

  /**
   * Whether this load carries no usable baseWidth, so it must be derived from the stored box width.
   * Checks the INCOMING object, not {@link baseWidth}: once the textbox exists, baseWidth reads the
   * live textbox, and copying a stored 0 into it makes fabric grow the box to its widest word — a
   * non-zero width that would otherwise hide the missing value (items saved before baseWidth
   * existed store 0; ~0.8% of sampled production posters).
   */
  #lacksStoredBaseWidth(textItemObject: DeepPartial<TextItemObject>): boolean {
    if (textItemObject.baseWidth !== undefined) {
      return !(textItemObject.baseWidth > 0);
    }
    return this.detachedBaseWidth === 0;
  }

  private updateBaseWidth(): void {
    if (this.fabricObject.width === 0) {
      this.baseWidth = this.textStyles.fontSize * (this.text.length / 2);
      this.fabricObject.width = this.baseWidth + this.padding * 2 + DEFAULT_STROKE_WIDTH;
    } else {
      this.baseWidth = this.calculateBaseWidthFromWidth();
    }
  }

  private calculateBaseWidthFromWidth(): number {
    if (this.fabricObject.width) {
      return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
    }

    return 0;
  }

  public isTextEmpty(): boolean {
    return this.text === '';
  }

  public override getFonts(withVariation: boolean): string[] {
    const fonts = [withVariation ? getFontFamilyNameForVariations(this.textStyles.fontFamily, this.textStyles.isBold, this.textStyles.isItalic) : this.textStyles.fontFamily];
    for (const run of this.styleRuns.runs) {
      if (run.styles.fontFamily === null && run.styles.isBold === null && run.styles.isItalic === null) {
        continue;
      }
      const fontFamily = run.styles.fontFamily ?? this.textStyles.fontFamily;
      const font = withVariation ? getFontFamilyNameForVariations(fontFamily, run.styles.isBold ?? this.textStyles.isBold, run.styles.isItalic ?? this.textStyles.isItalic) : fontFamily;
      if (!fonts.includes(font)) {
        fonts.push(font);
      }
    }
    return fonts;
  }

  protected async hasUnsupportedText(): Promise<boolean> {
    const supported = this.getFonts(true).map((family) => {
      return fontSupportsText(family, this.text);
    });

    const hasSupportedTextArray = await Promise.all(supported);
    return hasSupportedTextArray.some((isSupported) => {
      return !isSupported;
    });
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<TextItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);
    // this.fabricObject.set('shadow', null);
    //
    // if (obj.shadow !== undefined) {
    //   if (obj.shadow === null) {
    //     this.fabricTextbox.set('shadow', null);
    //   } else {
    //     const unserializedObj = await util.enlivenObjectEnlivables({
    //       shadow: {
    //         ...this.fabricObject.shadow,
    //         ...obj.shadow,
    //       },
    //     });
    //
    //     this.fabricTextbox.set('shadow', unserializedObj.shadow);
    //   }
    // }
  }

  public hasBackground(): boolean {
    return this.background.fill.hasFill();
  }

  public override async isPDFSafe(): Promise<boolean> {
    // Glyphs on a path export per character with their own transforms; svg-to-pdfkit does not place them.
    if (this.isShaped()) {
      return false;
    }
    if (this.getRichTextPdfRasterizeReason() !== null) {
      return false;
    }

    // These fonts fail to register in pdf. Investigate later why
    if (this.getFonts(false).some((fontFamily) => PDF_UNREGISTRABLE_FONT_FAMILIES.includes(fontFamily))) {
      return false;
    }

    if (this.fabricObject.shadow) {
      return false;
    }

    try {
      const incompatibleFont = await this.hasUnsupportedText();
      const incompatibleDecorations =
        (this.textStyles.letterSpacing > 0 && (this.textStyles.underLine || this.textStyles.lineThrough)) || // fabricJS breaks decoration on individual characters
        (this.textStyles.lineThrough && this.textStyles.underLine) ||
        this.background.fill.fillType === FillTypes.RADIAL_GRADIENT ||
        this.textStyles.stroke; // bug in svg-to-pdfkit does not treat decorations as a list, so need to rasterize if using both together

      return !PDF_UNSAFE_RTL_SCRIPTS.includes(this.textStyles.script) && !incompatibleFont && !incompatibleDecorations;
    } catch {
      return false;
    }
  }

  /** Style runs, per-line alignment, lists and gradient runs export as vector text; outlined, shadowed or decorated runs do not. */
  public getRichTextPdfRasterizeReason(): TextPdfRasterizeReason | null {
    return getRichTextPdfRasterizeReason({styleRuns: this.styleRuns.toObject(), baseStyles: this.textStyles});
  }

  protected hasCenterHorizontalAlignment(): boolean {
    return this.textStyles.textAlign === TextHorizontalAlignType.CENTER;
  }

  protected override isRoundedBorderSupported(): boolean {
    return false;
  }

  protected hasRightHorizontalAlignment(): boolean {
    return this.textStyles.textAlign === TextHorizontalAlignType.RIGHT;
  }

  public override toObject(): TextItemObject {
    return {
      ...super.toObject(),
      ...(this.getEditSessionGeometryAsShaped() ?? {}),
      shadow: this.fabricObject.shadow ? (this.fabricObject.shadow.toObject() as SerializedShadowOptions) : null,
      // shadow: null,
      textStyles: this.textStyles.toObject(),
      baseWidth: this.baseWidth,
      fontFamily: this.textStyles.fontFamily,
      verticalAlign: this.verticalAlign,
      verticalPadding: round(this.verticalPadding, NUM_FRACTION_DIGITS),
      padding: this.padding,
      background: this.background.toObject(),
      text: toWellFormed(this.text),
      styleRuns: this.styleRuns.toObject(),
      lineStyles: cloneDeep(this.lineStyles),
      shape: this.shape.toObject(),
    };
  }

  protected override onBeforeItemInitialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      addFonts([this.textStyles.getFontFamilyToLoad()], resolve, reject);
    });
  }

  /**
   * The box border is the item's base `border`, painted on the background rect instead of the
   * group: the base class's group stroke never applies to a text item (a group paints no stroke
   * and the stroke width would pad its box), the way the QR item paints its own container border.
   */
  protected override applyBorder(): void {
    this.setBackgroundParams();
  }

  public setBackgroundParams(): void {
    // The border's width is in canvas pixels and does not scale with the item, like every other
    // item border. Fabric's strokeUniform cannot do that here: it draws a child divided by the
    // group's total scaling but sizes its box by the child's own scale, so inside a scaled group
    // the rect overflows the box. The stroke is a plain local stroke of width ÷ scale instead,
    // recomputed on every scale step. It strokes INSIDE the group bounds — fabric's left/top
    // already position the stroke-inclusive box, so only the path shrinks by the stroke width —
    // and the group's dimensions and object cache never grow.
    const borderWidth = getTextBoxBorderWidth(this.border);
    const borderWidthInLocalUnits = borderWidth / this.#getEffectiveScaleForBorder();
    this.backgroundFabricObject.set({
      fill: this.background.fill.getFabricFill({width: this.fabricObject.width, height: this.fabricObject.height}),
      left: -this.fabricObject.width / 2,
      top: -this.fabricObject.height / 2,
      width: this.fabricObject.width - borderWidthInLocalUnits,
      height: this.fabricObject.height - borderWidthInLocalUnits,
      rx: this.background.rx,
      ry: this.background.ry,
      stroke: borderWidth > 0 ? rgbaToRgbaString(this.border.solidBorderColor) : null,
      strokeWidth: borderWidthInLocalUnits,
    });
  }

  /**
   * The scale the border's canvas-pixel width is divided by to get its local stroke width: the
   * group's total scaling, which for a text slide includes the slideshow it sits in.
   */
  #getEffectiveScaleForBorder(): number {
    const scale = this.fabricObject.getObjectScaling().x;
    return scale > 0 ? scale : 1;
  }

  public override async beforeInitFabricObject(): Promise<void> {
    await super.beforeInitFabricObject();
    await this.checkForBulletsFontLoad();
  }

  public getBaseWidth(): number {
    return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
  }

  public override onScaling(): void {
    this.updateFabricGroupHeight();
    this.updateFabricGroupWidth();
    this.setBackgroundParams();
    this.setTextChildPosition();

    super.onScaling();
  }

  protected override initEvents(): void {
    super.initEvents();
    this.fabricObject.on('mousedown:before', this.onMouseDownBefore.bind(this));
    this.fabricObject.on('mouseup', this.onMouseUp.bind(this));
    this.fabricObject.on('mouseup:before', this.onMouseUpBefore.bind(this));

    // TODO: Do this when working on spell check.
    this.fabricObject.on('deselected', this.onDeselected.bind(this));
  }

  protected onDeselected(): void {
    // TODO: When generation/view page flags are set
    // if (!postermywall.Core.isPosterGenerating && !postermywall.Core.isViewPage) {
    this.page.spellCheck.clearSquigglyLineStyle(this.fabricTextbox);
    // }
  }

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<Group> {
    if (!this.baseWidth && opts.width !== undefined) {
      this.baseWidth = opts.width - this.padding * 2 - this.getStrokeWidth();
    }

    const {shadow} = await util.enlivenObjectEnlivables({
      shadow: opts?.shadow ?? null,
    });

    this.fabricTextbox = new Textbox(this.text, {
      ...this.textStyles.getTextStyles(this.baseWidth, 100),
      statefullCache: true,
      width: this.baseWidth,
      perPixelTargetFind: true,
      // The textbox becomes the canvas' active object during in-place editing; these flags keep
      // it type-only there (no dragging/scaling, no control handles, just the editing frame).
      hasControls: false,
      hasBorders: true,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      // @ts-expect-error TODO: Remove this later.
      _fontSizeFraction: FONTSIZE_FRACTION[this.textStyles.fontFamily] ?? DEFAULT_FONT_SIZE_FRACTION,
    });

    this.backgroundFabricObject = new Rect({
      width: opts?.width ?? this.fabricTextbox.width,
      height: opts?.height ?? this.fabricTextbox.height,
      fill: this.background.fill.getFabricFill({width: opts?.width ?? this.fabricTextbox.width, height: opts?.height ?? this.fabricTextbox.height}),
      strokeWidth: 0,
      uniformScaling: false,
      uniformRoundness: true,
    });

    const fabricObject = new Group([this.backgroundFabricObject, this.fabricTextbox], {
      ...super.getCommonOptions(),
      ...this.#getGroupSizeAtInit(opts),
      useSelectedFlag: true,
      selected: false,
      caterCacheForTextChildren: true,
      perPixelTargetFind: true,
      layoutManager: new LayoutManager(new FixedLayout()),
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
    });
    fabricObject.on('selected', (): void => {
      this.#refreshShapeGuide();
    });
    fabricObject.on('deselected', (): void => {
      this.#refreshShapeGuide();
    });

    fabricObject.set('shadow', shadow);

    return fabricObject;
  }

  /**
   * The box a loaded item starts with. A plain box hugs its text, so it is laid out from the base
   * width. A shaped box-master's box IS the saved width and height — the font fits into it, so a
   * load that laid it out from the plain box's hug instead fitted the font into the wrong box and
   * shrank the text on every reload (a heart lost half its size per load, 2026-10-29).
   */
  #getGroupSizeAtInit(opts: InitItemOpts): {width: number; height: number} {
    if (this.#isShapedBoxMaster() && opts.width !== undefined && opts.height !== undefined) {
      return {width: opts.width, height: opts.height};
    }
    return {width: this.calculateGroupWidth(), height: this.calculateGroupHeight()};
  }

  public override async updateFabricObject(): Promise<void> {
    // `baseWidth` reads the live textbox width, and the option pass below lays the textbox out
    // with its OLD text styles still applied: a write that shrinks the width and the letter
    // spacing together (undo of a spacing that grew the box) re-grew the box in that pass, and
    // reading the width afterwards kept it grown (reported 2026-10-12). Capture the written width first.
    const baseWidth = this.baseWidth;
    const textboxWidth = this.#getEditSessionWrapWidth() ?? baseWidth;
    await super.updateFabricObject();
    this.fabricTextbox.set(this.getOptionsForTextItem());
    this.fabricTextbox.set({
      ...this.textStyles.getTextStyles(this.fabricTextbox.width, this.fabricTextbox.height),
      // Not applying width here breaks undo for text item width not getting restored for font size that causes different text item width (rev. 62225) (has to do with the order in which styles are applied to fabric textbox).
      text: this.#getFabricText(),
      width: textboxWidth,
    });
    // have to reapply text item fontStyle because it gives bad fabric Textbox height otherwise (Rev. 63548) (has to do with the order in which styles are applied to fabric textbox).
    this.fabricTextbox.set('fontStyle', this.textStyles.getFontStyle());

    if (this.fabricTextbox.hiddenTextarea) {
      this.fabricTextbox.hiddenTextarea.value = this.text;
    }

    this.applyTextStroke(this.fabricTextbox);
    applyStyleRunsToFabricTextbox(this.fabricTextbox, this.styleRuns.runs, this.textStyles);
    applyLineStylesToFabricTextbox(this.fabricTextbox, this.lineStyles);
    // Every layout above the run compile measured the OLD per-character styles still on fabric
    // (a run scaled up by a whole-box font size that is now being undone), and fabric never
    // shrinks a box it grew. Size the box for the NEW styles: the written width, grown only if
    // a word is wider than it.
    this.fabricTextbox.set('width', textboxWidth);
    this.fabricTextbox.initDimensions();
    this.#applyShapeToTextbox(baseWidth);
    const shapeWrite = this.shapeWriteInFlight;
    if (shapeWrite !== null && this.#isShapedBoxMaster()) {
      this.#fitBoxToShapeWrite(shapeWrite);
    } else {
      this.#fitShapedTextToBox();
      this.#fitShapedTextToPath();
    }
    // The dimensions are set again in case sidebar-driven modifications (font size etc) changed
    // the width — unless the box is the master, which the text fits into.
    if (!this.#isShapedBoxMaster()) {
      this.updateFabricGroupHeight();
      this.updateFabricGroupWidth();
    }
    this.#growGroupToEditSessionPreview();
    this.#replaceEditSessionPreviewInGrownGroup();

    this.setBackgroundParams();
    this.setTextChildPosition();
    this.fabricTextbox.setCoords();
    if (this.getEditingTextbox()) {
      this.page.spellCheck.checkSpell(this.fabricTextbox, true).catch(noop);
    }
    this.fabricObject.setCoords();
  }

  public override getScaleForShadow(): number {
    return 1;
  }

  protected checkForFontsLoad(): Promise<void> {
    return new Promise((resolve, reject) => {
      // getFonts(true) is the base variation plus every style run's family×(bold,italic)
      // variation — run fonts must be loaded before the runs compile onto fabric.
      addFonts(this.getFonts(true), resolve, reject);
    });
  }

  protected async checkForBulletsFontLoad(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.hasUnorderedListLine()) {
        loadBulletsFont(BULLETS_FONT_FAMILY, () => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  public getActiveText(): string {
    return this.text;
  }

  protected getOptionsForTextItem(): Record<string, any> {
    return {
      text: this.#getFabricText(),
      // shadow: this.fabricObject.shadow,
      cursorWidth: 2,
      width: this.baseWidth,
      cursorColor: this.getCursorColor(),
      _fontSizeFraction:
        this.textStyles.fontFamily in FONTSIZE_FRACTION ? FONTSIZE_FRACTION[this.textStyles.fontFamily as keyof typeof FONTSIZE_FRACTION] : DEFAULT_FONT_SIZE_FRACTION,
    };
  }

  protected override applyShadowToFabricObject(): void {
    this.fabricObject.set('shadow', this.fabricObject.shadow);
  }

  /**
   * A shape write from the sidebar (the shape key alone, no position) and a Size write grow or
   * shrink the box round the text's anchor, the way typing grows a box: the horizontal middle
   * stays under the text and the edge the vertical alignment pins stays put (keeping the centre
   * climbs a heading off the page; keeping the top-left corner lurches a ring sideways). A load,
   * an undo (which restores the position itself) and a slide inside a slideshow keep the
   * position they were given.
   */
  #keepsAnchorThroughUpdate(textItemObject: DeepPartial<TextItemObject>, isLoad: boolean): boolean {
    const resizesTheBox = this.#isShapeWrite(textItemObject, isLoad) || this.#getBoxSizeForFontSizeWrite(textItemObject, isLoad) !== null;
    return resizesTheBox && textItemObject.left === undefined && textItemObject.top === undefined && !isLoad && !this.isChildItem();
  }

  /**
   * A box sized by its path stays where its path is: a drawn path where the user drew it, a spiral
   * round its centre. A write that keeps the item on that shape but carries no position of its own
   * (new text, a font, a refit, the reshape after an edit session) keeps the path's origin on the
   * page, however the box around the text changes. A load, an undo (which restores the position
   * itself), a slide, and a switch to another shape keep nothing.
   */
  #getPathOriginToKeep(textItemObject: DeepPartial<TextItemObject>, isLoad: boolean, isInTextEditMode: boolean): Point | null {
    const writtenShapeType = textItemObject.shape?.type;
    const staysOnItsPath = this.shape.isBoxSizedByPath() && (writtenShapeType === undefined || writtenShapeType === this.shape.type);
    if (!staysOnItsPath || isLoad || isInTextEditMode || this.isChildItem() || textItemObject.left !== undefined || textItemObject.top !== undefined) {
      return null;
    }
    return this.#getPathOriginOnPage() ?? this.pathOriginBeforeEditSession;
  }

  /** Where the drawn path's (0, 0) — the top-left of the stroke as drawn — renders on the page. Null while the textbox follows no drawn path (an edit session, a plain item, a generated shape). */
  public getDrawnPathOriginOnPage(): Point | null {
    return this.shape.isDrawnPath() ? this.#getPathOriginOnPage() : null;
  }

  /**
   * Where the path's (0, 0) renders on the page — the top-left of a drawn stroke, the centre of a
   * generated shape: fabric draws a path point at `point − pathOffset` from the textbox's centre.
   * Null while the textbox follows no path.
   */
  #getPathOriginOnPage(textbox: Textbox = this.fabricTextbox): Point | null {
    const origin = this.#getPathOriginInGroup(textbox);
    return origin === null ? null : new FabricPoint(origin.x, origin.y).transform(this.fabricObject.calcTransformMatrix());
  }

  #getPathOriginInGroup(textbox: Textbox = this.fabricTextbox): Point | null {
    const {path} = textbox;
    if (!path) {
      return null;
    }
    // The box's own middle, not getRelativeCenterPoint's, which adds half the stroke.
    return {x: textbox.left + textbox.width / 2 - path.pathOffset.x, y: textbox.top + textbox.height / 2 - path.pathOffset.y};
  }

  #movePathOriginTo(origin: Point): void {
    const renderedOrigin = this.#getPathOriginOnPage();
    if (renderedOrigin === null) {
      return;
    }
    this.fabricObject.set({left: this.fabricObject.left + origin.x - renderedOrigin.x, top: this.fabricObject.top + origin.y - renderedOrigin.y});
    this.fabricObject.setCoords();
    this.page.fabricCanvas.requestRenderAll();
  }

  /**
   * A pick of a shape or a move of its sliders, as opposed to a load, an undo (which carries the
   * box too) or a resize. Read before the write is copied in: which box dimensions hug the text
   * now, and whether the write picks another shape.
   */
  #getShapeWriteInFlight(textItemObject: DeepPartial<TextItemObject>, isLoad: boolean): TextShapeWriteInFlight | null {
    if (!this.#isShapeWrite(textItemObject, isLoad)) {
      return null;
    }
    const inset = this.getBoxInset();
    return {
      // A drag keeps the room the box had when it started, whatever its moves did to the box since.
      hugging: this.shapeSliderDrag?.hugging ?? {
        width: Math.abs(this.fabricObject.width - (this.fabricTextbox.width + inset)) < 1,
        height: Math.abs(this.fabricObject.height - (this.fabricTextbox.height + inset)) < 1,
      },
      isPick: textItemObject.shape?.type !== undefined && textItemObject.shape.type !== this.shape.type,
    };
  }

  #isShapeWrite(textItemObject: DeepPartial<TextItemObject>, isLoad: boolean): boolean {
    return textItemObject.shape !== undefined && textItemObject.width === undefined && textItemObject.height === undefined && !isLoad && !this.getEditingTextbox();
  }

  /**
   * A shape write keeps the font — a shape shapes the text, it does not size it — and the box
   * follows the shape: a dimension that hugged the text before the write hugs the new shape, one
   * the user gave room with a handle keeps that room unless the shape needs more, so a slider
   * moved back restores the same box. Within a drag the room is the box the drag started from,
   * so a move that grew the box is undone by the move back. A curve or a wave picked hugs on
   * both sides whatever the last shape's box held, keeping only the room below the text the
   * plain box had: a figure's room means nothing to a shape the text sizes, and fitting the font
   * into it shrank the font.
   */
  #fitBoxToShapeWrite({hugging, isPick}: TextShapeWriteInFlight): void {
    const hugsThePickedShape = isPick && !this.shape.isOutlineShape();
    const inset = this.getBoxInset();
    const neededWidth = this.fabricTextbox.width + inset;
    const neededHeight = this.fabricTextbox.height + inset + (hugsThePickedShape ? this.verticalPadding : 0);
    const roomToKeep = this.shapeSliderDrag?.box ?? this.fabricObject;
    this.fabricObject.set({
      width: hugging.width || hugsThePickedShape ? neededWidth : getSizeGrownToHold(roomToKeep.width, neededWidth),
      height: hugging.height || hugsThePickedShape ? neededHeight : getSizeGrownToHold(roomToKeep.height, neededHeight),
    });
  }

  /**
   * On a shaped item the box is the master and the font fits into it, so a font size written by
   * the Size control would fit straight back. The write resizes the box with the font instead —
   * the part of the box the glyphs scale, by the font's factor; the fixed margins stay — and the
   * fit then lands on the size asked for. Null for every other write.
   */
  #getBoxSizeForFontSizeWrite(textItemObject: DeepPartial<TextItemObject>, isLoad: boolean): {width: number; height: number} | null {
    const fontSize = textItemObject.textStyles?.fontSize;
    if (fontSize === undefined || isLoad || this.getEditingTextbox() || !this.#isShapedBoxMaster() || textItemObject.width !== undefined || textItemObject.height !== undefined || this.textStyles.fontSize <= 0) {
      return null;
    }
    return this.#scaleShapedBox(this.#getShapedBoxInnerSize(), fontSize / this.textStyles.fontSize);
  }

  /**
   * A shaped text item that is not a slide: its box is the master, kept from the plain box the
   * shape was applied to (and from the user's resizes), and the font fits into it — the
   * requirement's "adding or removing text fits the text in the shape by adjusting font size".
   * A text slide's box belongs to its slideshow, so a slide keeps its font and lets the shape size
   * itself; so does a box sized by its path (a drawn path, and a spiral, which the design keeps at
   * its font and winds further out as the text grows).
   */
  #isShapedBoxMaster(): boolean {
    return this.isShaped() && !this.shape.isBoxSizedByPath() && !this.isChildItem();
  }

  /** The room inside a box (the item's own unless given) for the shaped glyphs: the box minus the padding and the textbox's stroke. */
  #getShapedBoxInnerSize(box: {width: number; height: number} = this.fabricObject): {width: number; height: number} {
    const inset = this.getBoxInset();
    return {width: box.width - inset, height: box.height - inset};
  }

  /** What the box adds round the textbox: the padding on both sides and the textbox's stroke. Soft-private: see {@link baseWidth}. */
  private getBoxInset(): number {
    return this.padding * 2 + this.fabricTextbox.strokeWidth;
  }

  /** The margins of a shaped layout that do not scale with the font: the box margin and the stroke, on both sides. */
  #getShapedFixedMargin(textbox: Textbox): number {
    return (SHAPED_BOX_MARGIN + textbox.strokeWidth) * 2;
  }

  /** The box that holds a shaped layout scaled by a font factor: the part the glyphs scale, the fixed margins and the inset round it. */
  #scaleShapedBox(size: {width: number; height: number}, factor: number): {width: number; height: number} {
    const fixedMargin = this.#getShapedFixedMargin(this.fabricTextbox);
    const inset = this.getBoxInset();
    return {
      width: Math.max(0, size.width - fixedMargin) * factor + fixedMargin + inset,
      height: Math.max(0, size.height - fixedMargin) * factor + fixedMargin + inset,
    };
  }

  /**
   * The shaped layout scales with the font (the path is as long as the text, the glyph margins
   * are ems), so the factor that brings a laid-out textbox's box to `inner` is a font factor.
   * Null when the layout already fits, or when there is nothing to fit.
   */
  #getShapedFitFactor(textbox: Textbox, inner: {width: number; height: number}): number | null {
    const fixedMargin = this.#getShapedFixedMargin(textbox);
    const naturalWidth = textbox.width - fixedMargin;
    const naturalHeight = textbox.height - fixedMargin;
    if (naturalWidth <= 0 || naturalHeight <= 0 || inner.width <= fixedMargin || inner.height <= fixedMargin) {
      return null;
    }
    const factor = Math.min((inner.width - fixedMargin) / naturalWidth, (inner.height - fixedMargin) / naturalHeight);
    return Math.abs(factor - 1) < SHAPED_FIT_TOLERANCE ? null : factor;
  }

  /** Writes the font size that fits the shaped text into the box, scaling the runs with it, and lays the text out again. */
  #fitShapedTextToBox(): void {
    if (!this.#isShapedBoxMaster() || this.getEditingTextbox()) {
      return;
    }
    if (this.shape.isOutlineShape()) {
      // The figure takes the box's size, so a box that changed lays the text out again before the fit reads it.
      this.fabricTextbox.initDimensions();
    }
    this.#fitShapedFont((textbox) => this.#getShapedBoxFitFactor(textbox, this.#getShapedBoxInnerSize()));
    this.#growBoxToFlooredShapedText();
  }

  /** The font factor that fits the shaped text into `inner`: a figure's text into the room the figure's outline gives it, any other shape's layout into the box. */
  #getShapedBoxFitFactor(textbox: Textbox, inner: {width: number; height: number}): number | null {
    return this.shape.isOutlineShape() ? this.#getOutlineRoomFitFactor(textbox, inner) : this.#getShapedFitFactor(textbox, inner);
  }

  /**
   * A figure fills its box, so the fit asks whether the text fits the covered part of that
   * figure's outline. Text longer than it comes down to its length (the figure then grows a
   * little, as the smaller glyphs reach less far, and the letters spread into the difference);
   * shorter text keeps its font and spreads. The font never grows from this fit — the Size
   * control and the corner handles grow it — so a box or a text shrunk and grown back leaves the
   * letters at the size the fit took them to, as the design keeps the font the user set.
   */
  #getOutlineRoomFitFactor(textbox: Textbox, inner: {width: number; height: number}): number | null {
    const textLength = this.#getShapedPlainTextLength(textbox);
    const room = this.#getOutlineRoomAt(textbox, inner, textbox.fontSize, textLength);
    if (room === null || textLength <= 0) {
      return null;
    }
    if (room < textLength * (1 - SHAPED_FIT_TOLERANCE)) {
      return room / textLength;
    }
    return this.#getOutlineRegrowFactor(textbox, inner, textLength, room);
  }

  /**
   * How far the laid-out glyphs reach past the whole figure on both sides together; the em model
   * when no figure is laid out yet. The figure's own bounds, not the path's: the path covers the
   * slider's share of the outline and leaves the seam out, so it is narrower than the figure the
   * box holds.
   */
  #getMeasuredGlyphReachRoundFigure(textbox: Textbox): {width: number; height: number} {
    const modelReach = textbox.fontSize * SHAPED_GLYPH_ASCENT_EM * 2;
    if (!textbox.path) {
      return {width: modelReach, height: modelReach};
    }
    const textLength = this.#getShapedPlainTextLength(textbox);
    const figure = getTextShapeOutline({shape: this.shape.toObject(), textLength, fontSize: textbox.fontSize, outlineFitBox: this.#getOutlineFitBox(textbox), letterCount: this.#getShapedLetterCount(textbox)});
    if (figure === null) {
      return {width: modelReach, height: modelReach};
    }
    const fixedMargin = this.#getShapedFixedMargin(textbox);
    return {
      width: Math.max(0, textbox.width - fixedMargin - this.#getShapedListGutter(textbox) - figure.width),
      height: Math.max(0, textbox.height - fixedMargin - figure.height),
    };
  }

  /**
   * The factor that brings a font the fit shrank back up as the text gets shorter: to the font the
   * user set, or as far as the covered outline holds the text, whichever comes first (the design's
   * "user types more, font size decreases and vice versa"). The room shrinks as the font grows —
   * the glyphs reach further outside the figure, so the figure that fits the box gets smaller —
   * while the text grows with it, so the font that just fills the room is found by bisection with
   * the text's length scaled with the font; the fit's next pass measures the real glyphs. Null
   * while no fit shrank the font, and while the text fills its room.
   */
  #getOutlineRegrowFactor(textbox: Textbox, inner: {width: number; height: number}, textLength: number, room: number): number | null {
    const setFontSize = this.fontSizeBeforeShapeFit;
    const fontSize = textbox.fontSize;
    if (setFontSize === null || setFontSize <= fontSize * (1 + SHAPED_FIT_TOLERANCE) || room <= textLength * (1 + SHAPED_FIT_TOLERANCE)) {
      return null;
    }
    const getSpareRoomAt = (candidateFontSize: number): number => {
      const textLengthAtCandidate = (textLength * candidateFontSize) / fontSize;
      return (this.#getOutlineRoomAt(textbox, inner, candidateFontSize, textLengthAtCandidate) ?? 0) - textLengthAtCandidate;
    };
    if (getSpareRoomAt(setFontSize) >= 0) {
      return setFontSize / fontSize;
    }
    let fillingFontSize = fontSize;
    let overflowingFontSize = setFontSize;
    for (let step = 0; step < OUTLINE_REGROW_BISECTION_STEPS; step++) {
      const middleFontSize = (fillingFontSize + overflowingFontSize) / 2;
      if (getSpareRoomAt(middleFontSize) >= 0) {
        fillingFontSize = middleFontSize;
      } else {
        overflowingFontSize = middleFontSize;
      }
    }
    return fillingFontSize / fontSize;
  }

  /** The covered outline's room for the text at `fontSize` in the box's figure, `textLength` being the text's length at that size. */
  #getOutlineRoomAt(textbox: Textbox, inner: {width: number; height: number}, fontSize: number, textLength: number): number | null {
    return getOutlineRoomInBox({shape: this.shape.toObject(), textLength, fontSize, outlineFitBox: this.#getOutlineFitBoxAt(textbox, inner, fontSize), letterCount: this.#getShapedLetterCount(textbox)});
  }

  /**
   * A figure's fit keeps the font it shrinks from, so the text can come back up to it; a fit that
   * brings the text back to it lands on it exactly and forgets it. Other shapes keep no such font.
   * Returns the font size the fit lands on.
   */
  #settleFontSizeThroughOutlineFit(fontSizeBefore: number, fittedFontSize: number): number {
    if (!this.shape.isOutlineShape()) {
      return fittedFontSize;
    }
    if (fittedFontSize < fontSizeBefore) {
      this.fontSizeBeforeShapeFit ??= fontSizeBefore;
      return fittedFontSize;
    }
    const setFontSize = this.fontSizeBeforeShapeFit;
    if (setFontSize !== null && fittedFontSize >= setFontSize * (1 - SHAPED_FIT_TOLERANCE)) {
      this.fontSizeBeforeShapeFit = null;
      return setFontSize;
    }
    return fittedFontSize;
  }

  /**
   * The font the user set is the one a figure's fit comes back up to: a font size written from
   * outside the fit (the user, an undo, a load) replaces it, and a shape that is no figure fits
   * its text its own way, so either write forgets it.
   */
  #forgetFontSizeBeforeShapeFit(textItemObject: DeepPartial<TextItemObject>): void {
    const writtenFontSize = textItemObject.textStyles?.fontSize;
    const setsNewFontSize = writtenFontSize !== undefined && writtenFontSize !== this.textStyles.fontSize;
    const writtenShapeType = textItemObject.shape?.type;
    const leavesTheFigures = writtenShapeType !== undefined && writtenShapeType !== this.shape.type && !isOutlineShapeType(writtenShapeType);
    if (setsNewFontSize || leavesTheFigures) {
      this.fontSizeBeforeShapeFit = null;
    }
  }

  /**
   * The room a circle, square or heart may fill in a box-master item: the box the layout keeps
   * to (the session's, while editing) less the glyphs' reach outside the outline, the margins and
   * the list gutter. Undefined when the text sizes the figure itself.
   */
  #getOutlineFitBox(textbox: Textbox): TextShapeOutlineFitBox | undefined {
    if (!this.shape.isOutlineShape() || !this.#isShapedBoxMaster()) {
      return undefined;
    }
    // Through a slider drag the figure fills the box the drag started from, not the box its last move left.
    return this.#getOutlineFitBoxAt(textbox, this.shapedEditSession?.keptBoxInner ?? this.#getShapedBoxInnerSize(this.shapeSliderDrag?.box), textbox.fontSize);
  }

  #getOutlineFitBoxAt(textbox: Textbox, inner: {width: number; height: number}, fontSize: number): TextShapeOutlineFitBox {
    const reach = fontSize * SHAPED_GLYPH_ASCENT_EM + SHAPED_BOX_MARGIN + textbox.strokeWidth;
    return {width: inner.width - this.#getShapedListGutter(textbox) - reach * 2, height: inner.height - reach * 2};
  }

  /** The text's own width along the path, without the spread the layout added: fabric's measured width carries the added gap after every letter but the last. */
  #getShapedPlainTextLength(textbox: Textbox): number {
    const spread = Math.max(0, textbox.charSpacing - this.textStyles.letterSpacing);
    return textbox.calcTextWidth() - spread * Math.max(0, this.#getShapedLetterCount(textbox) - 1);
  }

  /** The letters on the shaped line, spaces included, as fabric laid them out. */
  #getShapedLetterCount(textbox: Textbox): number {
    // eslint-disable-next-line no-underscore-dangle -- fabric keeps the laid-out graphemes only as _textLines
    return textbox._textLines[0]?.length ?? 0;
  }

  /**
   * A drawn path is as long as the user drew it: text longer than it shrinks until it fits (down
   * to the font floor), text shorter than it keeps its font and sits centred on the path.
   */
  #fitShapedTextToPath(): void {
    if (!this.shape.isDrawnPath() || this.getEditingTextbox()) {
      return;
    }
    this.#fitShapedFont((textbox) => this.#getDrawnPathFitFactor(textbox));
  }

  /** The fit loop both shaped fits share: writes each pass's font to the model (the runs scale with it) and lays the text out again. */
  #fitShapedFont(getFactor: (textbox: Textbox) => number | null): void {
    const textbox = this.fabricTextbox;
    for (let pass = 0; pass < SHAPED_FIT_MAX_PASSES; pass++) {
      const factor = getFactor(textbox);
      if (factor === null) {
        break;
      }
      const fittedFontSize = this.#settleFontSizeThroughOutlineFit(this.textStyles.fontSize, clamp(this.textStyles.fontSize * factor, this.#getShapedFitFontSizeFloor(), FONT_SIZE_MAX));
      if (fittedFontSize === this.textStyles.fontSize) {
        break;
      }
      this.copyVals({textStyles: {fontSize: fittedFontSize}});
      // Assigned, not set: the run compile and the layout below lay the text out once.
      textbox.fontSize = fittedFontSize;
      this.applyTextStroke(textbox);
      applyStyleRunsToFabricTextbox(textbox, this.styleRuns.runs, this.textStyles);
      textbox.initDimensions();
    }
  }

  /**
   * How small the fit may take the font. A drawn path goes down to the Size control's own minimum,
   * so long text keeps shrinking instead of running past the path's end, where fabric wraps it back
   * over its start; only at that minimum can letters overlap. A generated shape keeps a readable
   * floor and grows its box instead, since its path always matches the text.
   */
  #getShapedFitFontSizeFloor(): number {
    return this.shape.isDrawnPath() ? FONT_SIZE_MIN : SHAPED_FIT_FONT_SIZE_MIN;
  }

  /** The length of the drawn path the text follows, in the item's units; null while it follows none. */
  public getDrawnPathLength(): number | null {
    const {path} = this.fabricTextbox;
    return path && this.shape.isDrawnPath() ? getPathTotalLength(path) : null;
  }

  /**
   * The font size at which the text, as it is laid out on its drawn path now (runs and all), runs
   * the path's whole length — grown or shrunk — no larger than `maxFontSize`. Null while the
   * textbox follows no drawn path.
   */
  public getFontSizeFillingDrawnPath(maxFontSize: number): number | null {
    const pathLength = this.getDrawnPathLength();
    const textLength = this.fabricTextbox.calcTextWidth();
    if (pathLength === null || textLength <= 0) {
      return null;
    }
    return Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, maxFontSize, (this.textStyles.fontSize * pathLength) / textLength));
  }

  /** The width `text` takes on one line in this item's whole-box styles at `fontSize`, measured on a scratch textbox so the item is untouched. */
  public measureTextWidthOnOneLine(text: string, fontSize: number = this.textStyles.fontSize): number {
    const scratch = new Textbox(text, {...this.textStyles.getTextStyles(UNWRAPPED_TEXT_MEASURE_WIDTH, 0), fontSize, width: UNWRAPPED_TEXT_MEASURE_WIDTH});
    return scratch.calcTextWidth();
  }

  /** The font factor that brings text longer than its drawn path down to the path's length; null when the text already fits. */
  #getDrawnPathFitFactor(textbox: Textbox): number | null {
    const {path} = textbox;
    if (!path) {
      return null;
    }
    const pathLength = getPathTotalLength(path);
    const textLength = textbox.calcTextWidth();
    if (pathLength <= 0 || textLength <= pathLength * (1 + SHAPED_FIT_TOLERANCE)) {
      return null;
    }
    return pathLength / textLength;
  }

  /**
   * When the font floor wins — more text than the box can hold at a readable size — the box
   * grows to hold the floored layout instead, so the selection and the cache keep enclosing the
   * glyphs instead of cutting a curve drawn past its box.
   */
  #growBoxToFlooredShapedText(): void {
    const textbox = this.fabricTextbox;
    const inset = this.getBoxInset();
    this.fabricObject.set({
      width: getSizeGrownToHold(this.fabricObject.width, textbox.width + inset),
      height: getSizeGrownToHold(this.fabricObject.height, textbox.height + inset),
    });
  }

  /** The preview's fit, which writes nothing: the preview's own font and run sizes scale by the factor. */
  #fitPreview(preview: Textbox, runs: TextStyleRunObject[], plainWidth: number, getFactor: (textbox: Textbox) => number | null): void {
    let scaledRuns = runs;
    for (let pass = 0; pass < SHAPED_FIT_MAX_PASSES; pass++) {
      const factor = getFactor(preview);
      if (factor === null) {
        return;
      }
      scaledRuns = scaleRunFontSizes(scaledRuns, factor);
      preview.fontSize = clamp(preview.fontSize * factor, this.#getShapedFitFontSizeFloor(), FONT_SIZE_MAX);
      this.applyTextStroke(preview);
      applyStyleRunsToFabricTextbox(preview, scaledRuns, this.textStyles);
      this.#layoutShapedTextbox(preview, plainWidth);
    }
  }

  /**
   * The text fabric lays out. A shaped item puts everything on ONE line, so its hard line breaks
   * become spaces — a newline and a space are both one grapheme, so every run and selection index
   * stays valid. An edit session always types on the plain box, with the real breaks.
   */
  #getFabricText(): string {
    return this.isShaped() && !this.getEditingTextbox() ? joinLinesForShapedLayout(this.text) : this.text;
  }

  /**
   * Lays the textbox out along the shape's path, or back onto the plain box. Fabric's Textbox keeps
   * wrapping by its width even with a path, so the text is first laid out on one unbounded line
   * (the path is made exactly that long) and the path is set. Fabric centres the PATH in the box,
   * but the glyphs reach past it on one side only (outward on an arch), so the path is then shifted
   * by the glyphs' own centre and the box sized by hand to hug the glyphs, like a plain box hugs
   * its lines. Editing happens on the plain box, so an active edit session is left alone; the shape
   * comes back when it exits.
   */
  #applyShapeToTextbox(baseWidth: number): void {
    const textbox = this.fabricTextbox;
    if (!this.isShaped() || this.getEditingTextbox()) {
      // The override may already have laid the box out plain (a NONE write reaches it through
      // the style sets above), so the test is whether the shaped layout owned the box, not
      // whether a path is still on it.
      if (this.#uninstallShapedLayout()) {
        textbox.set({path: undefined, waveWarp: undefined, width: baseWidth, textAlign: this.textStyles.textAlign, charSpacing: this.textStyles.letterSpacing});
        applyLineStylesToFabricTextbox(textbox, this.lineStyles);
      }
      if (this.isShaped() && this.getEditingTextbox() && this.shapedEditSession?.shapeKey !== this.#getShapeKey()) {
        this.#layOutEditSessionForShapeWrite();
      }
      if (!this.isShaped() && this.getEditingTextbox() && this.shapedEditSession !== null) {
        this.#endShapedLayoutOfEditSession(baseWidth);
      }
      this.#syncEditSessionShapedPreview();
      return;
    }
    this.detachedBaseWidth = baseWidth;
    // The squiggles the plain box carried are per-character styles that would draw along the
    // path; a shaped box is never marked (the plain box is, while it is edited), so they come off.
    this.page.spellCheck.clearSquigglyLineStyle(textbox);
    this.#installShapedLayout();
    textbox.initDimensions();
  }

  /**
   * The width the typing box of a shaped edit session wraps at: the session's own, not the stored
   * base width, which is the plain box's and stays detached while the shape is on (the debounced
   * typing commit would otherwise lay the typing box out past the box the session keeps to, and
   * the next keystroke would grow the box to it). Null outside a shaped session, and for the pick
   * that starts one (its layout sets the width below).
   */
  #getEditSessionWrapWidth(): number | null {
    return this.isShaped() && this.shapedEditSession !== null ? this.editSessionBaseWidth : null;
  }

  /**
   * The shape came off in the session (an undo of the pick that put it on): the preview goes and
   * the typing box wraps at the plain width written, as a session opened on the plain box does.
   */
  #endShapedLayoutOfEditSession(baseWidth: number): void {
    this.#closeShapedEditSession();
    this.editSessionBaseWidth = baseWidth;
  }

  /**
   * While shaped, the textbox's own `initDimensions` is replaced on the instance by the shaped
   * layout, so EVERY relayout fabric runs on its own — a style write from spell check clearing its
   * squiggles on deselect, a font that lands late, a `set` of any layout property — lays the text
   * out on the path again instead of wrapping the single line into the tight box, which would
   * draw the wrapped lines on top of each other along the path.
   */
  #installShapedLayout(): void {
    if (Object.hasOwn(this.fabricTextbox, 'initDimensions')) {
      return;
    }
    Object.defineProperty(this.fabricTextbox, 'initDimensions', {
      configurable: true,
      writable: true,
      value: (): void => {
        this.#layoutShapedTextbox(this.fabricTextbox, this.detachedBaseWidth);
      },
    });
  }

  /** True when the shaped layout owned the textbox until now. */
  #uninstallShapedLayout(): boolean {
    if (!Object.hasOwn(this.fabricTextbox, 'initDimensions')) {
      return false;
    }
    Reflect.deleteProperty(this.fabricTextbox, 'initDimensions');
    return true;
  }

  /**
   * The shaped layout itself: one unbounded line (the path is made exactly as long as it), the
   * path set and measured, the path then shifted by the glyphs' own centre and the box sized to
   * hug them. Fabric's layout runs through the prototype here, never through the instance override
   * this method backs.
   */
  #layoutShapedTextbox(textbox: Textbox, plainWidth: number): void {
    if (this.isLayingOutShape) {
      return;
    }
    this.isLayingOutShape = true;
    try {
      const layOutPlain = (): void => {
        Textbox.prototype.initDimensions.call(textbox);
      };
      textbox.path = undefined;
      textbox.waveWarp = undefined;
      textbox.width = UNWRAPPED_TEXT_MEASURE_WIDTH;
      // The spread of the last layout comes off before the text is measured.
      textbox.charSpacing = this.textStyles.letterSpacing;
      // A path as long as the text leaves alignment nothing to do, a justify alignment would
      // stretch the line's spaces to the unbounded measuring width, and the list marker is
      // placed by the alignment offset. The textbox stays left-aligned for as
      // long as it is shaped; the model's alignment goes back on it with the plain layout.
      textbox.textAlign = TextHorizontalAlignType.LEFT;
      // A line's own alignment override would win over the box's; its semantic passenger stays
      // for the harvest, only the raw key fabric positions by goes.
      for (const lineRecord of Object.values(textbox.lineStyles)) {
        delete lineRecord.textAlign;
      }
      layOutPlain();
      const textLength = textbox.calcTextWidth();
      const pathInput = {shape: this.shape.toObject(), textLength, fontSize: this.textStyles.fontSize, outlineFitBox: this.#getOutlineFitBox(textbox), letterCount: this.#getShapedLetterCount(textbox)};
      const pathData = getTextShapePathData(pathInput);
      if (pathData === null) {
        textbox.textAlign = this.textStyles.textAlign;
        textbox.width = plainWidth;
        layOutPlain();
        return;
      }
      const path = new Path(pathData, {visible: false});
      textbox.path = path;
      textbox.pathAlign = 'baseline';
      // A wave's glyphs are not turned along its path: the fork bends the flat line by the sine
      // (fancy text's wave) and the path draws the guide and sizes the box.
      textbox.waveWarp = this.shape.type === TextShapeType.WAVE ? (getTextWaveWarp(this.shape.amount, this.shape.count, textLength) ?? undefined) : undefined;
      textbox.setPathInfo();
      const seamGap = this.#getSeamGapOnPath(textLength);
      const spread = this.#spreadLettersAlongPath(textbox, path, textLength + seamGap);
      // Half the seam gap and half a letter gap in from the path's start, so the word space a
      // closed ring keeps at its seam, and the gap the spacing leaves after the last letter,
      // split evenly round the text: the seam's gap is the word space plus every other gap.
      textbox.pathStartOffset = this.#getPathStartOffset(path, textLength) + seamGap / 2 + spread / 2;
      this.#styleShapeGuide(path, textbox, textLength);
      layOutPlain();
      const glyphBounds = this.#getShapedContentBounds(textbox, path, getTextShapeOutline(pathInput)?.bounds ?? null);
      // A listed line keeps the gutter plain lists reserve: the box grows by it on the left and
      // the glyphs sit to its right, so the marker the fork draws at the box's top-left never
      // lands on text running down that edge (a square's or a circle's).
      const listGutter = this.#getShapedListGutter(textbox);
      path.pathOffset = new FabricPoint(path.pathOffset.x + (glyphBounds.minX + glyphBounds.maxX) / 2 - listGutter / 2, path.pathOffset.y + (glyphBounds.minY + glyphBounds.maxY) / 2);
      layOutPlain();
      const margin = SHAPED_BOX_MARGIN + textbox.strokeWidth;
      textbox.width = glyphBounds.maxX - glyphBounds.minX + margin * 2 + listGutter;
      textbox.height = glyphBounds.maxY - glyphBounds.minY + margin * 2;
      textbox.dirty = true;
    } finally {
      this.isLayingOutShape = false;
    }
  }

  /**
   * A path that runs longer than what the text takes on it (the text, and a closed curve's seam
   * gap) on a shape that spreads gets the letters spread evenly along it: the extra length is
   * split into one gap after every letter, added to the item's own letter spacing on the textbox
   * only. Returns the gap, 0 when nothing spreads.
   */
  #spreadLettersAlongPath(textbox: Textbox, path: Path, takenLength: number): number {
    if (!this.shape.spreadsLettersAlongPath()) {
      return 0;
    }
    const slack = getPathTotalLength(path) - takenLength;
    const letterCount = this.#getShapedLetterCount(textbox);
    if (slack < SHAPED_SPREAD_MIN_SLACK || letterCount === 0) {
      return 0;
    }
    const spread = slack / letterCount;
    textbox.charSpacing = this.textStyles.letterSpacing + spread;
    return spread;
  }

  /**
   * The word space a closed curve's ring carries at its seam, in the text's units. A figure's path
   * already leaves its seam gap out, so only the curve's full ring has one to place.
   */
  #getSeamGapOnPath(textLength: number): number {
    return this.shape.type === TextShapeType.CURVE ? getCurveSeamGap(this.shape.amount, this.textStyles.fontSize, textLength) : 0;
  }

  /**
   * A generated path is as long as the text (or longer, with the letters spread along it); a drawn
   * one is as long as the user drew it, so text shorter than it sits along it by the item's
   * alignment: from the path's start (left, and justify, which fabric cannot spread along a path),
   * centred on it, or ending at its end.
   */
  #getPathStartOffset(path: Path, textLength: number): number {
    if (!this.shape.isDrawnPath()) {
      return 0;
    }
    const slack = Math.max(0, getPathTotalLength(path) - textLength);
    switch (this.textStyles.textAlign) {
      case TextHorizontalAlignType.LEFT:
      case TextHorizontalAlignType.JUSTIFY:
        return 0;
      case TextHorizontalAlignType.CENTER:
        return slack / 2;
      case TextHorizontalAlignType.RIGHT:
        return slack;
      default:
        return assertNever(this.textStyles.textAlign);
    }
  }

  /**
   * While the item is selected its path shows under the text as a guide, so the user sees the
   * shape and how more text will run (the requirement's "path visible to the user"): a solid line
   * under a circle, square, wave or spiral, as the design draws them, and a dashed one under text
   * that does not yet fill a drawn path. A curve, a heart, the edit session's preview and an
   * unselected item draw none.
   */
  #styleShapeGuide(path: Path, textbox: Textbox, textLength: number): void {
    const guideStyle = this.shape.getGuideStyle();
    const fontSize = this.textStyles.fontSize;
    path.set({
      visible: textbox === this.fabricTextbox && this.#isSelectedOnCanvas() && this.#isShapeGuideShown(guideStyle, path, textLength),
      fill: '',
      stroke: SHAPE_GUIDE_COLOR,
      strokeWidth: fontSize * SHAPE_GUIDE_WIDTH_EM,
      strokeDashArray: guideStyle === TextShapeGuideStyle.DASHED_WHILE_UNFILLED ? DRAWN_PATH_GUIDE_DASH_EM.map((dash) => dash * fontSize) : null,
      objectCaching: false,
    });
  }

  #isShapeGuideShown(guideStyle: TextShapeGuideStyle, path: Path, textLength: number): boolean {
    switch (guideStyle) {
      case TextShapeGuideStyle.NONE:
        return false;
      case TextShapeGuideStyle.SOLID:
        return true;
      case TextShapeGuideStyle.DASHED_WHILE_UNFILLED:
        return textLength < getPathTotalLength(path) - DRAWN_PATH_GUIDE_SLACK;
      default:
        return assertNever(guideStyle);
    }
  }

  #isSelectedOnCanvas(): boolean {
    return this.page.getSelectedItems().includes(this);
  }

  /**
   * Shows the whole circle a curve bends its text round, as a hairline over the canvas, until
   * {@link hideCurveCircleGuide}: the design's temporary guide while the Curve slider is dragged
   * (a typed value shows none). It follows every slider move, and draws nothing while the item is
   * not a curve, is being edited, or holds a single letter.
   */
  public showCurveCircleGuide(): void {
    if (this.curveCircleGuideListener !== null) {
      return;
    }
    const listener = ({ctx}: {ctx: CanvasRenderingContext2D}): void => {
      this.#drawCurveCircleGuide(ctx);
    };
    this.curveCircleGuideListener = listener;
    this.page.fabricCanvas.on('after:render', listener);
    this.page.fabricCanvas.requestRenderAll();
  }

  public hideCurveCircleGuide(): void {
    if (this.curveCircleGuideListener === null) {
      return;
    }
    this.page.fabricCanvas.off('after:render', this.curveCircleGuideListener);
    this.curveCircleGuideListener = null;
    this.page.fabricCanvas.requestRenderAll();
  }

  #drawCurveCircleGuide(ctx: CanvasRenderingContext2D): void {
    const circle = this.#getCurveCircleInGroup();
    if (circle === null) {
      return;
    }
    const matrix = util.multiplyTransformMatrices(this.page.fabricCanvas.viewportTransform, this.fabricObject.calcTransformMatrix());
    const matrixScale = Math.sqrt(Math.abs(matrix[0] * matrix[3] - matrix[1] * matrix[2]));
    if (matrixScale <= 0) {
      return;
    }
    ctx.save();
    ctx.transform(...matrix);
    ctx.beginPath();
    ctx.arc(circle.center.x, circle.center.y, circle.radius, 0, Math.PI * 2);
    ctx.lineWidth = CURVE_CIRCLE_GUIDE_SCREEN_WIDTH / matrixScale;
    ctx.strokeStyle = SHAPE_GUIDE_COLOR;
    ctx.stroke();
    ctx.restore();
  }

  /** The circle a curve's path lies on, in the group's units: centred on the path's origin, where the curve's arc is built round. */
  #getCurveCircleInGroup(): {center: Point; radius: number} | null {
    if (this.shape.type !== TextShapeType.CURVE || this.getEditingTextbox() || Array.from(this.text.trim()).length < CURVE_CIRCLE_GUIDE_MIN_LETTERS) {
      return null;
    }
    const center = this.#getPathOriginInGroup();
    const radius = getCurveRadius(this.shape.amount, this.fabricTextbox.calcTextWidth(), this.textStyles.fontSize);
    return center === null || radius === null ? null : {center, radius};
  }

  /** Selection changes toggle the shape's guide: the path's style, no relayout. */
  #refreshShapeGuide(): void {
    const textbox = this.fabricTextbox;
    if (this.shape.getGuideStyle() === TextShapeGuideStyle.NONE || this.getEditingTextbox() || !textbox.path) {
      return;
    }
    this.#styleShapeGuide(textbox.path, textbox, textbox.calcTextWidth());
    textbox.dirty = true;
    this.fabricObject.dirty = true;
    this.page.fabricCanvas.requestRenderAll();
  }

  /**
   * What the shaped box encloses, relative to the textbox centre: the glyphs, and for a box sized
   * by its path the whole path too — the region the user drew, the spiral with the turn it shows
   * ahead of the text — so text that does not fill the path neither shrinks the box nor hides the
   * unfilled part of the guide. A figure's box is the whole figure, lobes and all, plus the reach
   * a glyph standing on it has, all round: exactly what the fit takes off the box to find the
   * figure again, so the box a figure leaves holds the same figure on the next layout. The
   * measured glyphs play no part there: a box sized by them falls short where no glyph stands at
   * an extreme, and overshoots by a turned glyph box's corner where one does, and either way the
   * next layout refits the font or grows the ring.
   */
  #getShapedContentBounds(textbox: Textbox, path: Path, figureBounds: TextShapeBounds | null): {minX: number; maxX: number; minY: number; maxY: number} {
    if (figureBounds !== null && this.shape.isOutlineShape()) {
      const reach = textbox.fontSize * SHAPED_GLYPH_ASCENT_EM;
      return {
        minX: figureBounds.minX - path.pathOffset.x - reach,
        maxX: figureBounds.maxX - path.pathOffset.x + reach,
        minY: figureBounds.minY - path.pathOffset.y - reach,
        maxY: figureBounds.maxY - path.pathOffset.y + reach,
      };
    }
    if (this.shape.type === TextShapeType.WAVE) {
      return this.#getWaveContentBounds(textbox, path);
    }
    const glyphBounds = this.#measureShapedGlyphBounds(textbox);
    if (!this.shape.isBoxSizedByPath()) {
      return glyphBounds;
    }
    return {
      minX: Math.min(glyphBounds.minX, -path.width / 2),
      maxX: Math.max(glyphBounds.maxX, path.width / 2),
      minY: Math.min(glyphBounds.minY, -path.height / 2),
      maxY: Math.max(glyphBounds.maxY, path.height / 2),
    };
  }

  /**
   * What a wave encloses, relative to the textbox centre: the flat line's width, as the warp keeps
   * it, and its glyphs' reach above the highest crest and below the deepest trough of the sine
   * the baseline follows. The path's box is the sine's: as wide as the line, twice the amplitude
   * tall, drawn `pathOffset` from the centre.
   */
  #getWaveContentBounds(textbox: Textbox, path: Path): {minX: number; maxX: number; minY: number; maxY: number} {
    return {
      minX: -path.pathOffset.x,
      maxX: path.width - path.pathOffset.x,
      minY: -path.height / 2 - textbox.fontSize * SHAPED_GLYPH_ASCENT_EM,
      maxY: path.height / 2 + textbox.fontSize * SHAPED_GLYPH_DESCENT_EM,
    };
  }

  /** The width the shaped line's list marker reserves on the left, 0 when the line carries no list. */
  #getShapedListGutter(textbox: Textbox): number {
    // eslint-disable-next-line no-underscore-dangle -- the fork exposes the list gutter only through its own line helpers
    return textbox._getRealLineReservedWidth(0);
  }

  /**
   * The box every glyph on the path reaches, relative to the textbox centre: each glyph is a box
   * as wide as its advance, {@link SHAPED_GLYPH_ASCENT_EM} above the path and
   * {@link SHAPED_GLYPH_DESCENT_EM} below it, turned to the path's angle where it sits. Fabric's
   * char box carries the letter spacing after the glyph, and (pmw-72) anchors and draws the glyph
   * centred on its own ink, so the ink is the box less the spacing, either side of the anchor.
   */
  #measureShapedGlyphBounds(textbox: Textbox): {minX: number; maxX: number; minY: number; maxY: number} {
    textbox.getLineWidth(0);
    const bounds = {minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity};
    const spacing = textbox.charSpacing;
    // eslint-disable-next-line no-underscore-dangle -- fabric exposes measured char boxes only as __charBounds
    for (const charBox of textbox.__charBounds[0] ?? []) {
      if (charBox.renderLeft === undefined || charBox.renderTop === undefined || charBox.angle === undefined || charBox.width === 0) {
        continue;
      }
      const inkHalfWidth = (charBox.width - spacing) / 2;
      const inkStart = -inkHalfWidth;
      const inkEnd = inkHalfWidth;
      const ascent = charBox.height * SHAPED_GLYPH_ASCENT_EM;
      const descent = charBox.height * SHAPED_GLYPH_DESCENT_EM;
      const cosine = Math.cos(charBox.angle);
      const sine = Math.sin(charBox.angle);
      for (const [cornerX, cornerY] of [
        [inkStart, -ascent],
        [inkEnd, -ascent],
        [inkEnd, descent],
        [inkStart, descent],
      ]) {
        const x = charBox.renderLeft + cornerX * cosine - cornerY * sine;
        const y = charBox.renderTop + cornerX * sine + cornerY * cosine;
        bounds.minX = Math.min(bounds.minX, x);
        bounds.maxX = Math.max(bounds.maxX, x);
        bounds.minY = Math.min(bounds.minY, y);
        bounds.maxY = Math.max(bounds.maxY, y);
      }
    }
    if (!Number.isFinite(bounds.minX)) {
      return {minX: 0, maxX: 0, minY: 0, maxY: 0};
    }
    return bounds;
  }

  /**
   * An edit session types on the plain box, inside the curved text's own bounds: the path comes
   * off, the real line breaks return and the text wraps at the shaped box's width (not the stored
   * base width, so the selection box does not jump), with the runs and line records compiled
   * afresh for those lines. A faded copy laid out on the shape goes under it as the preview.
   */
  #unshapeTextboxForEditSession(): void {
    const textbox = this.fabricTextbox;
    if (!textbox.path) {
      return;
    }
    this.pathOriginBeforeEditSession = this.shape.isBoxSizedByPath() ? this.#getPathOriginOnPage() : null;
    const boxInner = this.#isShapedBoxMaster() ? this.#getShapedBoxInnerSize() : {width: textbox.width, height: textbox.height};
    const wrapWidth = boxInner.width;
    this.#uninstallShapedLayout();
    textbox.set({path: undefined, waveWarp: undefined, text: this.text, width: wrapWidth, textAlign: this.textStyles.textAlign, charSpacing: this.textStyles.letterSpacing});
    applyStyleRunsToFabricTextbox(textbox, this.styleRuns.runs, this.textStyles);
    applyLineStylesToFabricTextbox(textbox, this.lineStyles);
    textbox.set('width', wrapWidth);
    textbox.initDimensions();
    this.editSessionBaseWidth = wrapWidth;
    this.#openShapedEditSession({keptBoxInner: boxInner, keptBoxAnchorOnPage: this.#getAnchorOnPage()});
    this.#fitBoxToEditingTextbox(textbox);
  }

  /**
   * A shape picked or its slider moved while the item is being edited — on a plain item, or in
   * place of the shape it had: the session is laid out as one opened on that shape is. The preview
   * is laid out at the font, as a shape write outside a session is, the box the session keeps to
   * becomes the one the shape needs, anchored where a write outside a session keeps the item, and
   * the typing box wraps inside it (fitting the preview into the typing box or the last shape's
   * box would take the font with it). The session's exit puts that box back.
   */
  #layOutEditSessionForShapeWrite(): void {
    const keptBoxAnchorOnPage = this.shapedEditSession?.keptBoxAnchorOnPage ?? this.#getAnchorOnPage();
    // The last shape's path means nothing to the new shape; a path-sized pick keeps its own, below.
    this.pathOriginBeforeEditSession = null;
    if (this.shapedEditSession === null) {
      this.#openShapedEditSession({keptBoxInner: null, keptBoxAnchorOnPage});
    } else {
      this.shapedEditSession.shapeKey = this.#getShapeKey();
      this.shapedEditSession.keptBoxAnchorOnPage = keptBoxAnchorOnPage;
      this.shapedEditSession.keptBoxInner = null;
      this.#syncEditSessionShapedPreview();
    }
    const session = this.shapedEditSession;
    const editingTextbox = this.getEditingTextbox();
    if (session === null || editingTextbox === null) {
      return;
    }
    session.keptBoxInner = {width: session.preview.width, height: session.preview.height};
    this.#layEditSessionOutInKeptBox(editingTextbox);
    this.#placeEditSessionShapedPreview(session.preview);
    if (this.shape.isBoxSizedByPath()) {
      this.pathOriginBeforeEditSession = this.#getPathOriginOnPage(session.preview);
    }
  }

  /** The shape as the preview was laid out for it: a change of type or of a slider lays the session out afresh. */
  #getShapeKey(): string {
    return JSON.stringify(this.shape.toObject());
  }

  /**
   * The session's typing box wraps as wide as the kept box and the group holds both, the kept box
   * at its top-left as a session opened on the shape has it (the typing box grows the group from
   * there), so the two sessions look the same.
   */
  #layEditSessionOutInKeptBox(editingTextbox: Textbox): void {
    const kept = this.shapedEditSession?.keptBoxInner ?? null;
    if (kept === null) {
      return;
    }
    editingTextbox.set('width', kept.width);
    editingTextbox.initDimensions();
    this.editSessionBaseWidth = kept.width;
    const inset = this.getBoxInset();
    const keptHeight = kept.height + inset;
    this.fabricObject.set({width: kept.width + inset, height: Math.max(keptHeight, editingTextbox.height + inset)});
    const keptCentre = this.getEditSessionKeptBoxCentreOnPage(kept);
    if (keptCentre !== null) {
      const keptToGroupCentre = new FabricPoint(0, (this.fabricObject.height - keptHeight) / 2).transform(this.fabricObject.calcTransformMatrix(), true);
      this.fabricObject.setPositionByOrigin(keptCentre.add(keptToGroupCentre), 'center', 'center');
    }
    this.fabricObject.setCoords();
    this.setTextChildPosition();
    this.setBackgroundParams();
    editingTextbox.setCoords();
  }

  /** Opens the session: the preview goes into the group under the typing box, laid out on the shape. */
  #openShapedEditSession(kept: Pick<TextShapedEditSession, 'keptBoxInner' | 'keptBoxAnchorOnPage'>): void {
    const preview = new Textbox('', {
      ...this.getOptionsForTextItem(),
      opacity: SHAPED_EDIT_PREVIEW_OPACITY,
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      perPixelTargetFind: false,
      objectCaching: false,
    });
    this.fabricObject.insertAt(SHAPED_EDIT_PREVIEW_GROUP_INDEX, preview);
    // Fabric adds an object to a group as if it came from the canvas, so it hands the preview the
    // INVERSE of the group's scale, angle and flips; the preview lives in group space like the
    // textbox beside it.
    preview.set({scaleX: 1, scaleY: 1, angle: 0, flipX: false, flipY: false});
    this.shapedEditSession = {preview, shapeKey: this.#getShapeKey(), ...kept};
    this.#syncEditSessionShapedPreview();
  }

  #closeShapedEditSession(): void {
    const session = this.shapedEditSession;
    this.shapedEditSession = null;
    if (session !== null) {
      this.fabricObject.remove(session.preview);
    }
  }

  /**
   * Lays the preview out again from the text, styles and runs currently on the editing textbox and
   * the item's shape, and centres it in the group. Runs on every change of the session, and on
   * every model write that lands while the session is open (the shape sliders, a font change).
   */
  #syncEditSessionShapedPreview(): void {
    const session = this.shapedEditSession;
    const editingTextbox = this.getEditingTextbox() ?? this.fabricTextbox;
    if (session === null) {
      return;
    }
    const {preview} = session;
    preview.set({
      ...this.textStyles.getTextStyles(preview.width, preview.height),
      ...this.getOptionsForTextItem(),
      text: joinLinesForShapedLayout(editingTextbox.text),
      visible: this.isShaped(),
    });
    this.applyTextStroke(preview);
    const runs = harvestStyleRunsFromFabricTextbox(editingTextbox);
    applyStyleRunsToFabricTextbox(preview, runs, this.textStyles);
    this.#layoutShapedTextbox(preview, editingTextbox.width);
    const boxInner = session.keptBoxInner;
    if (boxInner !== null && this.#isShapedBoxMaster()) {
      this.#fitPreview(preview, runs, editingTextbox.width, (textbox) => this.#getShapedBoxFitFactor(textbox, boxInner));
      this.#followPreviewFitInEditSession(editingTextbox, preview, runs);
    } else if (this.shape.isDrawnPath()) {
      this.#fitPreview(preview, runs, editingTextbox.width, (textbox) => this.#getDrawnPathFitFactor(textbox));
      this.#followPreviewFitInEditSession(editingTextbox, preview, runs);
    }
    this.#placeEditSessionShapedPreview(preview);
  }

  /**
   * Where the box the session keeps to (its inner size plus the inset) is centred on the page,
   * from its anchor; null outside a session on a shaped box. Soft-private: see {@link baseWidth}.
   */
  private getEditSessionKeptBoxCentreOnPage(keptInner: {width: number; height: number}): FabricPoint | null {
    const anchor = this.shapedEditSession?.keptBoxAnchorOnPage ?? null;
    if (anchor === null) {
      return null;
    }
    const keptHeight = keptInner.height + this.getBoxInset();
    return anchor.add(new FabricPoint(0, keptHeight / 2 - this.getAnchorOffsetFromTop(keptHeight)).transform(this.fabricObject.calcTransformMatrix(), true));
  }

  /**
   * The box a shaped edit session serializes: the one its exit lays the shape out in — the box
   * the session keeps to where the box masters the shape, the preview's own where its path sizes
   * the box — not the group the typing box grows under the preview. Every commit the session
   * makes (the pick, the typed text) then records what the exit leaves, and the exit's own layout
   * pass changes nothing: it mints no undo step, and an undo or redo lays out to the same box as
   * the snapshot holds. Null outside a shaped session. Soft-private: see {@link baseWidth}.
   */
  private getEditSessionGeometryAsShaped(): Pick<TextItemObject, 'left' | 'top' | 'width' | 'height'> | null {
    const preview = this.shapedEditSession?.preview ?? null;
    if (preview === null || !this.isShaped() || this.isChildItem()) {
      return null;
    }
    const box = this.getEditSessionBoxAsShaped(preview);
    if (box === null) {
      return null;
    }
    // Fabric puts the origin half the scaled size from the centre, turned by the angle.
    const topLeft = box.centre.add(new FabricPoint((-box.width / 2) * this.fabricObject.scaleX, (-box.height / 2) * this.fabricObject.scaleY).rotate(util.degreesToRadians(this.fabricObject.angle)));
    return {
      left: round(topLeft.x, NUM_FRACTION_DIGITS),
      top: round(topLeft.y, NUM_FRACTION_DIGITS),
      width: round(box.width, NUM_FRACTION_DIGITS),
      height: round(box.height, NUM_FRACTION_DIGITS),
    };
  }

  /** The exit's box and its centre on the page: the preview's own where its path sizes the box, else the kept box where the session anchors it. Soft-private: see {@link baseWidth}. */
  private getEditSessionBoxAsShaped(preview: Textbox): {centre: FabricPoint; width: number; height: number} | null {
    const inset = this.getBoxInset();
    if (this.shape.isBoxSizedByPath()) {
      const centre = preview.getRelativeCenterPoint().transform(this.fabricObject.calcTransformMatrix());
      return {centre, width: preview.width + inset, height: preview.height + inset};
    }
    const kept = this.shapedEditSession?.keptBoxInner ?? null;
    const centre = kept === null ? null : this.getEditSessionKeptBoxCentreOnPage(kept);
    return kept === null || centre === null ? null : {centre, width: kept.width + inset, height: kept.height + inset};
  }

  /** The box's anchor on the page: its horizontal middle on the edge the vertical alignment pins — what typing keeps still, and so what a shape write keeps still. */
  #getAnchorOnPage(): FabricPoint {
    return this.fabricObject.getPositionByOrigin('center', this.verticalAlign);
  }

  #moveAnchorTo(anchor: FabricPoint): void {
    this.fabricObject.setPositionByOrigin(anchor, 'center', this.verticalAlign);
    this.fabricObject.setCoords();
  }

  /** How far down a box of this height its anchor lies: on the top edge, in the middle, or on the bottom edge. Soft-private: see {@link baseWidth}. */
  private getAnchorOffsetFromTop(height: number): number {
    return getOffsetByVerticalAlign(this.verticalAlign, height);
  }

  /** A group child sits relative to the group's centre, which moves when the group grows from its top-left corner, so the preview is put back where it belongs. */
  #replaceEditSessionPreviewInGrownGroup(): void {
    const preview = this.shapedEditSession?.preview ?? null;
    if (preview !== null) {
      this.#placeEditSessionShapedPreview(preview);
    }
  }

  /**
   * The preview sits where the shaped text will sit once the session ends: on its path where the
   * path was (a drawn path, a spiral), in the box the session keeps to — by the alignment in its
   * spare width and by the vertical alignment in its spare height, the box anchored on the page
   * since the typing box grows the group under it — or, with no kept box (a text slide), by the
   * alignment in the group or centred in it.
   */
  #placeEditSessionShapedPreview(preview: Textbox): void {
    const pathOrigin = this.pathOriginBeforeEditSession;
    const kept = this.shapedEditSession?.keptBoxInner ?? {width: preview.width, height: preview.height};
    const keptCentre = this.getEditSessionKeptBoxCentreOnPage(kept);
    if (this.shape.isBoxSizedByPath() && pathOrigin !== null && preview.path) {
      // The preview runs along the path where it was, wherever the session moves the typing box.
      const originInGroup = new FabricPoint(pathOrigin.x, pathOrigin.y).transform(util.invertTransform(this.fabricObject.calcTransformMatrix()));
      preview.set({left: originInGroup.x + preview.path.pathOffset.x - preview.width / 2, top: originInGroup.y + preview.path.pathOffset.y - preview.height / 2});
    } else if (keptCentre !== null) {
      const keptCentreInGroup = keptCentre.transform(util.invertTransform(this.fabricObject.calcTransformMatrix()));
      // A text child sits half its stroke outside the inner box, as the resting textbox does.
      preview.set({
        left: keptCentreInGroup.x - (kept.width + preview.strokeWidth) / 2 + this.#getSlackOffsetByAlignment(Math.max(0, kept.width - preview.width)),
        top: keptCentreInGroup.y - (kept.height + preview.strokeWidth) / 2 + this.#getVerticalSlackOffset(Math.max(0, kept.height - preview.height)),
      });
    } else if (this.#isShapedBoxMaster()) {
      preview.set({
        left: -this.fabricObject.width / 2 + this.padding + this.#getShapedHorizontalSlackOffset(preview.width),
        top: this.#getTextChildTop(preview.height + preview.strokeWidth),
      });
    } else {
      preview.set({left: -preview.width / 2, top: -preview.height / 2});
    }
    preview.setCoords();
  }

  /**
   * The font the preview fitted becomes the item's font during the session too — the model's
   * base size (its runs scale with it) and the editing textbox's own declarations — so the text
   * being typed shrinks or grows with its preview, and every state the session commits is already
   * fitted: an undo that restores one lays out to the same font and drifts nothing.
   */
  #followPreviewFitInEditSession(editingTextbox: Textbox, preview: Textbox, harvestedRuns: TextStyleRunObject[]): void {
    const fontSizeBefore = this.textStyles.fontSize;
    if (!(fontSizeBefore > 0) || Math.abs(preview.fontSize / fontSizeBefore - 1) < SHAPED_FIT_TOLERANCE) {
      return;
    }
    const fittedFontSize = this.#settleFontSizeThroughOutlineFit(fontSizeBefore, preview.fontSize);
    const factor = fittedFontSize / fontSizeBefore;
    this.copyVals({textStyles: {fontSize: fittedFontSize}});
    editingTextbox.set({fontSize: fittedFontSize});
    this.applyTextStroke(editingTextbox);
    const scaledRuns = scaleRunFontSizes(harvestedRuns, factor);
    applyStyleRunsToFabricTextbox(editingTextbox, scaledRuns, this.textStyles);
  }

  /**
   * A shaped session's box is the curved text's own: never smaller than the preview, however
   * short the typed lines are. Every writer of the group's size during the session ends here.
   */
  #growGroupToEditSessionPreview(): void {
    const preview = this.shapedEditSession?.preview ?? null;
    if (preview === null) {
      return;
    }
    const inset = this.getBoxInset();
    const keptInner = this.shapedEditSession?.keptBoxInner ?? {width: 0, height: 0};
    this.fabricObject.set({
      width: Math.max(this.fabricObject.width, preview.width + inset, keptInner.width + inset),
      height: Math.max(this.fabricObject.height, preview.height + inset, keptInner.height + inset),
    });
  }

  /**
   * The shape left the box for the session; a layout pass puts it back and fits the font to the
   * box again. The pass commits, because a session can leave the fitted font behind (a shape
   * slider moved mid-session, with no text change to commit it) — a pass that changes nothing
   * adds no history entry. The box the session kept to — the shape's own at entry, or the one a
   * shape written in the session needs — goes back where it was anchored first, since the typing
   * box may have grown the group past it and moved its anchor with it. A path-sized shape stays
   * where the preview had its path, read at the exit for the same reason.
   */
  #reshapeAfterEditSession(): void {
    const previewPathOriginOnPage = this.#getEditSessionPreviewPathOriginOnPage();
    const keptInner = this.shapedEditSession?.keptBoxInner ?? null;
    const keptAnchor = this.shapedEditSession?.keptBoxAnchorOnPage ?? null;
    // Read while the preview still detaches it: with the preview gone, the typing box laid out at
    // the session's wrap width would be read as the base width, and the pass would store that.
    const baseWidth = this.baseWidth;
    this.#closeShapedEditSession();
    if (!this.isShaped()) {
      return;
    }
    if (keptInner !== null && keptAnchor !== null) {
      const inset = this.getBoxInset();
      this.fabricObject.set({width: keptInner.width + inset, height: keptInner.height + inset});
      this.#moveAnchorTo(keptAnchor);
    }
    if (previewPathOriginOnPage !== null) {
      this.pathOriginBeforeEditSession = previewPathOriginOnPage;
    }
    // Fabric reports the session's text change as a canvas gesture, which the page commits once
    // the item has written itself back: this pass is that write-back, so the commit sees the
    // shape laid out, not the box between the session and the shape.
    this.canvasGestureWriteBack = this.updateFromObject(this.isChildItem() ? {} : {baseWidth}, {fireModifiedEvent: false}).catch((e: unknown) => {
      console.error(`Failed to lay the shape out after the edit session, Details:${JSON.stringify(e)}`);
    });
  }

  #getEditSessionPreviewPathOriginOnPage(): Point | null {
    const preview = this.shapedEditSession?.preview ?? null;
    return preview === null || !this.shape.isBoxSizedByPath() ? null : this.#getPathOriginOnPage(preview);
  }

  public getCursorColor(): string {
    return `#${rgbaToHex(this.textStyles.getFirstFillColor())}`;
  }

  protected override onFabricObjectModified(): void {
    this.updateFromObject(
      {
        verticalPadding: this.getVerticalPadding(),
      },
      {
        undoable: false,
        updateRedux: false,
      }
    ).catch((e) => {
      console.error(`Failed to update item on fabric object modified, Details:${JSON.stringify(e)}`);
    });
  }

  public applyTextStroke(fabricTextboxItem: Textbox): void {
    if (this.textStyles.stroke) {
      fabricTextboxItem.set('strokeWidth', this.textStyles.fontSize * this.textStyles.strokeWidth * TEXT_OUTLINE_STROKE_WIDTH_FACTOR);
      fabricTextboxItem.set('strokeLineJoin', 'round');
      fabricTextboxItem.set('paintFirst', 'stroke');
      fabricTextboxItem.set('stroke', rgbaToHexString(this.textStyles.strokeColor));
    } else if (!this.textStyles.isBold) {
      fabricTextboxItem.set('strokeWidth', DEFAULT_STROKE_WIDTH);
      fabricTextboxItem.set('strokeLineJoin', 'miter');
      fabricTextboxItem.set('paintFirst', 'fill');
      fabricTextboxItem.set('stroke', undefined);
    }
  }

  protected onMouseDownBefore(): void {
    if (this.fabricObject === this.page.activeSelection.getActiveObject()) {
      this.beforeEnterEditMode();
    }
  }

  // TODO: change the type when migrated to fabric v6.
  private onMouseUp(options: ObjectEvents['mouseup']): void {
    // Don't enter the editing mode if the mouse up event happened because of an action performed by control button
    const mouseEvent = options.e as MouseEvent;
    if (!this.enterEditModeOnMouseUp || options.transform?.actionPerformed || (mouseEvent.button && mouseEvent.button !== 1)) {
      this.enterEditModeOnMouseUp = true;
      return;
    }

    this.onEnterEditMode(options.e);
  }

  private onMouseUpBefore(options: ObjectEvents['mouseup:before']): void {
    if (this.page.longPressInitiated || options.target?.__corner) {
      this.enterEditModeOnMouseUp = false;
    }
  }

  public enterEditMode(e?: MouseEvent, {selectAllText = false}: EnterEditModeOpts = {}): void {
    this.beforeEnterEditMode();
    this.onEnterEditMode(e, selectAllText);
  }

  protected beforeEnterEditMode(): void {
    this.fabricObject.selected = true;
  }

  public onEnterEditMode(e?: TPointerEvent, selectAllText = false): void {
    if (this.getEditingTextbox()) {
      // Already in edit mode. In-place editing means clicks on the text itself also bubble this
      // far; only the mobile "soft keyboard was dismissed while the hidden textarea kept focus"
      // case needs a focus toggle to re-summon the keyboard.
      if (isEditorMobileVariant() && this.page.canvasPanOnTextEdit.isKeyboardHidden()) {
        this.refocusHiddenTextarea();
      }
      return;
    }
    if (this.fabricObject.selected) {
      this.enterInPlaceEditMode(e);
      placeEditSessionCaret({textbox: this.fabricTextbox, event: e, canvas: this.page.fabricCanvas, selectAllText: selectAllText || this.#isShowingTextOnPathPlaceholder()});
    }
  }

  /**
   * A drawn path still carrying its placeholder: the click that opens the edit session selects
   * all of it, so the first key typed replaces it ("click to replace it with your own text").
   */
  #isShowingTextOnPathPlaceholder(): boolean {
    return this.shape.isDrawnPath() && isTextOnPathPlaceholder(this.text);
  }

  /**
   * On mobile, tapping the text while already in edit mode only repositions the cursor; if the soft keyboard was
   * previously dismissed (e.g. via the device back button) the still-focused hidden textarea won't re-summon it.
   * Toggle focus so the keyboard re-opens, leaving the edit session and the current selection untouched.
   */
  private onEditingTextboxMouseUp(): void {
    if (!isEditorMobileVariant() || !this.getEditingTextbox() || !this.page.canvasPanOnTextEdit.isKeyboardHidden()) {
      return;
    }
    this.refocusHiddenTextarea();
  }

  /**
   * Forces a focus change on the edited textbox's hidden textarea so the mobile soft keyboard re-opens.
   * Fabric's `blur` handler only aborts the cursor animation (it does not exit editing), so the session and
   * its selection survive the toggle; the cursor animation is restarted afterwards.
   */
  private refocusHiddenTextarea(): void {
    const editingTextbox = this.getEditingTextbox();
    const hiddenTextarea = editingTextbox?.hiddenTextarea;
    if (!editingTextbox || !hiddenTextarea) {
      return;
    }
    hiddenTextarea.blur();
    hiddenTextarea.focus();
    editingTextbox.initDelayedCursor(true);
  }

  /**
   * Fabric paints the caret and the selection highlight on the top context from the textbox's
   * own render. Inside a cached group that render only runs when the cache is rebuilt, so any
   * render that clears the top context (hover borders, the grid, bleed and fold overlays mark it
   * dirty) wiped the highlight until the text changed — a double-clicked word looked unselected
   * while typing still replaced it. The group draws its children directly for the session.
   * The slideshow calls these for the edited text slide's own group, which caches inside the
   * (never cached) slideshow group.
   */
  /**
   * Fabric paints the caret and the selection highlight on the canvas's top context from the
   * textbox's render, which a cached group only runs while its cache is dirty. Any overlay that
   * clears the top context would then lose the highlight. The group keeps its cache for the
   * session — an uncached group renders every child with the group's shadow, so an item with a
   * box shadow would show a text shadow while editing — and is marked dirty before every render
   * instead, so the textbox renders each frame exactly as it does outside editing.
   */
  public keepGroupCacheFreshForEditSession(): void {
    this.stopKeepingGroupCacheFreshAfterEditSession();
    this.editSessionCacheRefreshDisposer = this.page.fabricCanvas.on('before:render', (): void => {
      this.fabricObject.dirty = true;
    });
  }

  public stopKeepingGroupCacheFreshAfterEditSession(): void {
    this.editSessionCacheRefreshDisposer?.();
    this.editSessionCacheRefreshDisposer = null;
  }

  private enterInPlaceEditMode(e?: TPointerEvent): void {
    if (isEditorMobileVariant()) {
      trackPosterBuilderGA4Events(GA4EventName.TEXT_EDIT, {[GA4EventParamName.TYPE]: 'OnCanvas'});
    }
    hideCustomControls(this.page.hashedID);
    const canvas = this.page.fabricCanvas;
    const textbox = this.fabricTextbox;
    textbox.__PMWID = this.uid;
    this.typedListPrefixConversion.startSession(textbox);
    this.editSessionBaseWidth = this.baseWidth;
    this.#unshapeTextboxForEditSession();
    // Editing happens in place: the group routes pointer events to the textbox only while a
    // session is active, so item-level dragging/targeting is unaffected outside editing.
    // Per-pixel hit testing is disabled on the textbox for the session so clicks between glyphs
    // position the cursor instead of falling through to the group (which would exit editing).
    this.fabricObject.set({subTargetCheck: true, interactive: true});
    textbox.set({perPixelTargetFind: false});
    this.keepGroupCacheFreshForEditSession();
    if (canvas instanceof Canvas) {
      canvas.setActiveObject(textbox);
    }

    this.editSessionListenerDisposers.push(
      textbox.on('editing:entered', this.onTextEditModeEntered.bind(this)),
      textbox.on('editing:exited', this.onEditingExited.bind(this)),
      textbox.on('changed', () => {
        this.onCanvasTextChange(this.fabricTextbox.text);
      }),
      textbox.on('selection:changed', this.onSelectionChanged.bind(this)),
      textbox.on('mouseup', this.onEditingTextboxMouseUp.bind(this))
    );

    textbox.enterEditing(e);
    this.page.canvasPanOnTextEdit.beforeTextEditEntered();
    // Fabric creates a fresh hidden textarea per editing session and removes it from the DOM on
    // exit, so these listeners live and die with the session.
    const hiddenTextarea = textbox.hiddenTextarea;
    if (hiddenTextarea) {
      this.hiddenTextareaPasteListener = (event: Event): void => {
        void this.onPasteText(event);
      };
      this.hiddenTextareaInputListener = (): void => {
        this.#applyPendingTypingStylesToTypedCharacters();
        this.onTextInput();
      };
      this.hiddenTextareaKeydownListener = (event: KeyboardEvent): void => {
        this.handleEditModeStyleShortcut(event);
        this.#handleListEnterKey(event);
        this.#handleListBackspaceKey(event);
      };
      this.hiddenTextareaBeforeInputListener = (event: Event): void => {
        if (event instanceof InputEvent) {
          this.pendingTypingStyles.armOnBeforeInput(event);
        }
      };
      hiddenTextarea.addEventListener('paste', this.hiddenTextareaPasteListener);
      hiddenTextarea.addEventListener('beforeinput', this.hiddenTextareaBeforeInputListener);
      hiddenTextarea.addEventListener('input', this.hiddenTextareaInputListener);
      hiddenTextarea.addEventListener('keydown', this.hiddenTextareaKeydownListener);
    }
    this.#publishTextSelectionStyles();
  }

  private onTextInput(): void {
    setTimeout(() => {
      this.page.canvasPanOnTextEdit.scrollCanvasToText();
    }, 150);
  }

  /**
   * Buffers styles at the collapsed caret for the NEXT characters typed (rich-text pending
   * styles) — nothing on the canvas changes until typing consumes the buffer. The style surfaces
   * immediately display the buffered value via the published summary overlay.
   */
  public setPendingTypingStyles(stylesPartial: Partial<TextRunStylesObject>): void {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    this.pendingTypingStyles.mergeAtCaret(editingTextbox, stylesPartial);
    void this.#repairTypedTextWhenPendingFontsLoad(editingTextbox, stylesPartial);
    this.#publishTextSelectionStyles();
  }

  /**
   * Typing cannot wait for a font face, so characters typed with a pending family or variation
   * before its face arrives are measured in the fallback face. Once the face loads, drop those
   * widths and lay the box out again around the glyphs it really draws.
   */
  async #repairTypedTextWhenPendingFontsLoad(editingTextbox: Textbox, stylesPartial: Partial<TextRunStylesObject>): Promise<void> {
    const loadedFontFamilies = await ensureSelectionApplyFontsAreLoaded(editingTextbox, stylesPartial, this.textStyles).catch((): string[] => []);
    if (loadedFontFamilies.length === 0 || this.getEditingTextbox() !== editingTextbox) {
      return;
    }
    remeasureTextboxForLoadedFonts(editingTextbox, loadedFontFamilies);
    this.#fitBoxToEditingTextbox(editingTextbox);
    this.page.fabricCanvas.requestRenderAll();
  }

  #remeasureForLoadedFonts(loadedFontFamilies: string[]): void {
    if (loadedFontFamilies.length === 0 || !this.fabricTextbox) {
      return;
    }
    remeasureTextboxForLoadedFonts(this.fabricTextbox, loadedFontFamilies);
  }

  #applyPendingTypingStylesToTypedCharacters(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox && this.pendingTypingStyles.consumeOnInput(editingTextbox, this.textStyles)) {
      editingTextbox.dirty = true;
      this.page.fabricCanvas.requestRenderAll();
      this.#publishTextSelectionStyles();
    }
  }

  public onSelectionChanged(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox) {
      this.pendingTypingStyles.discardOnSelectionChange(editingTextbox);
    }
    if (shouldShowTextSelectionPopup(editingTextbox)) {
      handleTextSelectionPopupMenu(editingTextbox);
    } else if (getIsSpellCheckEnabledFromStore() && editingTextbox) {
      void this.page.spellCheck.handleSuggestionPopupMenu(editingTextbox);
    } else {
      closeTextSelectionPopup();
    }
    this.#publishTextSelectionStyles();
  }

  public async onPasteText(e?: Event): Promise<void> {
    this.pendingTypingStyles.discard();
    const pastedText = await resolvePastedText(e);
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox && pastedText) {
      insertPastedTextIntoTextbox(editingTextbox, pastedText);
      this.onCanvasTextChange(editingTextbox.text);
    }
  }

  public onCanvasTextChange(text = ''): void {
    this.textCommit.cancel();

    const textbox = this.getEditingTextbox();
    if (textbox) {
      const convertedText = this.typedListPrefixConversion.convert(textbox, this.lineStyles, (): void => {
        this.#replayTextChangeOnceBulletsFontLoaded(textbox);
      })
        ? textbox.text
        : text;
      refreshLineListMarkersOnTextbox(textbox);
      this.page.spellCheck.clearSquigglyLineAroundCaret(textbox);
      const {newModelCoords, newVerticalPadding} = this.#fitBoxToEditingTextbox(textbox);
      this.textCommit.schedule(() => this.updateText(convertedText, newModelCoords, newVerticalPadding), TEXT_COMMIT_DEBOUNCE_MS);
    }
  }

  /**
   * Re-lays the group out around the edited textbox's current dimensions: the textbox keeps at
   * least its session width (growing only for a word that cannot wrap), the group follows, and
   * the origin moves so the edge the vertical alignment pins stays put.
   */
  #fitBoxToEditingTextbox(textbox: Textbox): {newModelCoords: ModelCoordinates; newVerticalPadding: number} {
    this.#syncEditSessionShapedPreview();
    const preview = this.shapedEditSession?.preview ?? null;
    if (preview !== null) {
      // The typed text wraps as wide as the curved preview, so the box is the curved text's own.
      this.editSessionBaseWidth = Math.max(this.editSessionBaseWidth ?? 0, preview.width);
    }
    const textboxDimensions = {
      width: textbox.width + textbox.strokeWidth,
      height: textbox.height + textbox.strokeWidth,
    };
    const heightWithoutVerticalPadding = textboxDimensions.height + this.padding * 2;
    // A shaped session's box is the preview's, not the typed lines': the model's padding stays as it is.
    const newVerticalPadding = preview !== null ? this.verticalPadding : Math.min(Math.max(this.fabricObject.height - heightWithoutVerticalPadding, 0), this.verticalPadding);
    // caching these 2 variables before updating group dimensions as they are needed in the next step.
    const textboxCenterPoint = textbox.getCenterPoint();
    const groupBottomLeft = this.fabricObject.aCoords.bl;

    textbox.set({width: Math.max(textbox.dynamicMinWidth ?? 0, this.editSessionBaseWidth ?? this.baseWidth)});

    this.updateGroupDimensions(textboxDimensions, newVerticalPadding);
    this.#growGroupToEditSessionPreview();

    const newModelCoords = this.getUpdatedModelCoords(textboxCenterPoint, groupBottomLeft);
    this.fabricObject.set({
      left: newModelCoords.x,
      top: newModelCoords.y,
    });
    this.fabricObject.setCoords();
    this.#replaceEditSessionPreviewInGrownGroup();
    this.setTextChildPosition();
    this.setBackgroundParams();
    textbox.setCoords();
    return {newModelCoords, newVerticalPadding};
  }

  /**
   * Enter on an EMPTY list row exits the list (clears that row's list entry instead of adding a
   * row), matching standard editors.
   */
  #handleListEnterKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    const {lineIndex} = editingTextbox.get2DCursorLocation(editingTextbox.selectionStart, true);
    const lineText = (editingTextbox.text ?? '').split('\n')[lineIndex] ?? '';
    if (lineText !== '' || !harvestLineStylesFromFabricTextbox(editingTextbox)[lineIndex]?.list) {
      return;
    }
    event.preventDefault();
    void this.applyListToTextSelection(null);
  }

  /**
   * Backspace at the START of a list row removes the row's bullet (clears its list entry)
   * instead of merging into the previous line, matching standard editors.
   */
  #handleListBackspaceKey(event: KeyboardEvent): void {
    if (event.key !== 'Backspace' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    const {lineIndex, charIndex} = editingTextbox.get2DCursorLocation(editingTextbox.selectionStart, true);
    if (charIndex !== 0 || !harvestLineStylesFromFabricTextbox(editingTextbox)[lineIndex]?.list) {
      return;
    }
    event.preventDefault();
    void this.applyListToTextSelection(null);
  }

  /** The bullets face arrived after a typed "- ": re-run the keystroke's text change, whose conversion is re-armed. */
  #replayTextChangeOnceBulletsFontLoaded(textbox: Textbox): void {
    if (this.getEditingTextbox() === textbox) {
      this.onCanvasTextChange(textbox.text ?? '');
    }
  }

  public updateText(
    text = '',
    modelNewCoordinates: ModelCoordinates = {
      x: this.fabricObject.getX(),
      y: this.fabricObject.getY(),
    },
    newVerticalPadding = this.verticalPadding
  ): Promise<void> {
    return this.updateFromObject({
      text,
      width: this.fabricObject.width,
      height: this.fabricObject.height,
      verticalPadding: newVerticalPadding,
      left: modelNewCoordinates.x,
      top: modelNewCoordinates.y,
      textStyles: this.#getScriptStylesForTextCommit(text),
      ...this.#getTextCommitStyleOverrides(),
    });
  }

  /**
   * The debounced commit's script/font detection, with an unchanged font family filtered out —
   * it is an echo of the current base, not a user write, and must not clear run font overrides
   * (see {@link #reconcileRunsWithBaseChange}).
   */
  #getScriptStylesForTextCommit(text: string): Partial<TextStylesObject> {
    const detectedScriptStyles = this.textStyles.getLanguageScriptAndFontForText(text);
    if (detectedScriptStyles.fontFamily === this.textStyles.fontFamily) {
      delete detectedScriptStyles.fontFamily;
    }
    return detectedScriptStyles;
  }

  /**
   * The run/line styles to commit alongside a text change. On-canvas edits mutate the edited
   * textbox's styles map, which fabric shifts wrap-aware on every insert/remove — harvesting it
   * is the single source of run indices. Outside an edit session the live textbox is equally
   * authoritative: wholesale replacements pre-shift it via
   * {@link TextItem.replaceWholeTextPreservingRuns} before committing.
   */
  #getTextCommitStyleOverrides(): DeepPartial<TextItemObject> {
    const textbox = this.getEditingTextbox() ?? this.fabricTextbox;
    return {styleRuns: harvestStyleRunsFromFabricTextbox(textbox), lineStyles: harvestLineStylesFromFabricTextbox(textbox)};
  }

  /**
   * Runs the debounced on-canvas text commit immediately. Called before anything reads or
   * overwrites the model mid-edit (external updates, editing exit), so the model and undo
   * history never see text older than the edited textbox's — and the cleared timer can never
   * fire later with stale text.
   */
  public flushPendingTextCommit(): Promise<void> {
    return this.textCommit.flush();
  }

  /**
   * Lands debounced typing in poster history before leaving edit mode, so poster undo/redo
   * cannot race a late text commit.
   */
  public async exitEditModeCommittingText(): Promise<void> {
    await this.flushPendingTextCommit();
    this.restoreDefaultState();
  }

  /**
   * Applies semantic style overrides to the current text-editing selection: the partial merges
   * into the selection's existing run passengers on the edited textbox, recompiles there, and
   * the harvested runs commit as ONE undoable update. No-ops without an edit session or with a
   * collapsed selection — callers fall back to a whole-item write for those.
   */
  public async applyStylesToTextSelection(stylesPartial: Partial<TextRunStylesObject>, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart >= editingTextbox.selectionEnd) {
      return;
    }
    await this.flushPendingTextCommit();
    await ensureSelectionApplyFontsAreLoaded(editingTextbox, stylesPartial, this.textStyles);
    applyRunStylesToTextboxSelection(editingTextbox, stylesPartial, this.textStyles, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({styleRuns: harvestStyleRunsFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * Aligns every real line the current text-editing selection touches (its own line for a
   * collapsed caret — alignment is a line property, so unlike the run styles it needs no
   * non-collapsed selection). Committed through the model as ONE undoable update.
   */
  public async applyAlignmentToTextSelection(textAlign: TextHorizontalAlignType, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    await this.flushPendingTextCommit();
    applyAlignmentToTextboxSelection(editingTextbox, textAlign, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({lineStyles: harvestLineStylesFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * Sets (or clears, list null) list membership on every real line the current text-editing
   * selection touches — its own line for a collapsed caret. Committed through the model as ONE
   * undoable update.
   */
  public async applyListToTextSelection(list: TextLineListObject | null, {undoable = true}: UpdateFromObjectOpts = {}): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    await this.flushPendingTextCommit();
    await this.#ensureBulletsFontForList(list);
    applyListToTextboxSelection(editingTextbox, list, editingTextbox.selectionStart, editingTextbox.selectionEnd);
    await this.updateFromObject({lineStyles: harvestLineStylesFromFabricTextbox(editingTextbox)}, {undoable});
  }

  /**
   * The whole-item form of a list write (the sidebar outside editing): every real line becomes
   * (or stops being) a list row, preserving per-line alignment overrides.
   */
  public buildLineStylesWithListForAllLines(list: TextLineListObject | null): Record<string, TextLineStylesObject> {
    const lineStyles: Record<string, TextLineStylesObject> = {};
    const lineCount = this.text.split('\n').length;
    for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
      const existing: TextLineStylesObject | undefined = this.lineStyles[lineIndex];
      const entry: TextLineStylesObject = {
        textAlign: existing?.textAlign ?? null,
        list: list ? withExistingIndent(list, existing?.list ?? null) : null,
      };
      if (entry.textAlign !== null || entry.list !== null) {
        lineStyles[lineIndex] = entry;
      }
    }
    return lineStyles;
  }

  /** Every real line's list state: the uniform list when all lines share one, null otherwise. */
  public getUniformWholeItemList(): TextLineListObject | null {
    return getUniformLineList(this.lineStyles, this.text);
  }

  public hasUnorderedListLine(): boolean {
    return Object.values(this.lineStyles).some((lineStyleObject) => lineStyleObject.list?.type === LIST_TYPES.UNORDERED);
  }

  /** Unordered markers are glyphs of the bullets face, which the SVG export names on their spans. */
  public override getSvgFontFamilies(): string[] {
    const fonts = this.getFonts(true);
    if (this.hasUnorderedListLine()) {
      fonts.push(BULLETS_FONT_FAMILY);
    }
    return fonts;
  }

  /** Unordered markers render in the bullets glyph font; load it before the projection measures them. */
  async #ensureBulletsFontForList(list: TextLineListObject | null): Promise<void> {
    if (list?.type !== LIST_TYPES.UNORDERED) {
      return;
    }
    await new Promise<void>((resolve) => {
      loadBulletsFont(BULLETS_FONT_FAMILY, () => {
        resolve();
      });
    });
  }

  /**
   * Slider semantics over a selection: every grapheme's effective size scales by one factor
   * from a baseline captured at drag start, preserving the size contrast within the selection.
   * `targetEffectiveFontSize` is in on-canvas units (font size × item scale), matching the size
   * control's displayed value; the factor is computed against the first grapheme's baseline.
   */
  public async applyFontSizeScaleToTextSelection(targetEffectiveFontSize: number, undoable: boolean): Promise<void> {
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox || editingTextbox.selectionStart >= editingTextbox.selectionEnd) {
      return;
    }
    await this.flushPendingTextCommit();
    const {selectionStart, selectionEnd} = editingTextbox;
    const baseline = this.#getOrCaptureSelectionFontSizeBaseline(editingTextbox, selectionStart, selectionEnd);
    const representativeEffectiveSize = baseline.effectiveFontSizes[0] * this.fabricObject.scaleX;
    const scaleFactor = representativeEffectiveSize > 0 ? targetEffectiveFontSize / representativeEffectiveSize : 1;
    const perGraphemeStyles = baseline.effectiveFontSizes.map((fontSize) => ({
      fontSize: clamp(fontSize * scaleFactor, FONT_SIZE_MIN, FONT_SIZE_MAX),
    }));

    applyPerGraphemeRunStylesToTextboxSelection(editingTextbox, this.textStyles, selectionStart, perGraphemeStyles);
    await this.updateFromObject({styleRuns: harvestStyleRunsFromFabricTextbox(editingTextbox)}, {undoable});
    if (undoable) {
      this.selectionFontSizeScaleBaseline = null;
    }
  }

  #getOrCaptureSelectionFontSizeBaseline(editingTextbox: Textbox, selectionStart: number, selectionEnd: number): {effectiveFontSizes: number[]} {
    const baseline = this.selectionFontSizeScaleBaseline;
    if (baseline?.selectionStart === selectionStart && baseline?.selectionEnd === selectionEnd) {
      return baseline;
    }
    this.selectionFontSizeScaleBaseline = {
      selectionStart,
      selectionEnd,
      effectiveFontSizes: getEffectiveFontSizesForSelection(editingTextbox, this.textStyles, selectionStart, selectionEnd),
    };
    return this.selectionFontSizeScaleBaseline;
  }

  #publishTextSelectionStyles(): void {
    const editingTextbox = this.getEditingTextbox();
    if (editingTextbox) {
      const summary = getTextSelectionStylesSummary(editingTextbox, this.textStyles, this.uid, this.getSerializedItemShadow());
      this.page.poster.redux.updateTextSelectionStyles(this.pendingTypingStyles.overlayOntoSummary(summary));
    }
  }

  /** The item-level shadow in its serialized form — the base a run's shadow override resolves against. */
  public getSerializedItemShadow(): SerializedShadowOptions | null {
    const itemShadow = this.fabricObject.shadow;
    return itemShadow ? (itemShadow.toObject() as SerializedShadowOptions) : null;
  }

  /**
   * Cmd/Ctrl+B/I/U while editing toggles the selection's styles (the whole item on a collapsed
   * caret). Lives on the session's hidden textarea because the app-level hotkeys layer ignores
   * textarea targets; Cmd/Ctrl+S stays the app-wide save shortcut. Bold follows the panel's
   * rule of being unavailable while the whole-box outline is on.
   */
  protected handleEditModeStyleShortcut(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
      return;
    }
    const key = event.key.toLowerCase();
    const toggledProperty = key === 'b' ? 'isBold' : key === 'i' ? 'isItalic' : key === 'u' ? 'underLine' : null;
    if (toggledProperty === null) {
      return;
    }
    event.preventDefault();
    if (toggledProperty === 'isBold' && this.textStyles.stroke) {
      return;
    }
    const editingTextbox = this.getEditingTextbox();
    if (!editingTextbox) {
      return;
    }
    // Both branches toggle off the caret/selection's effective value — the state the surfaces
    // display and the style typing would continue with.
    const summary = this.pendingTypingStyles.overlayOntoSummary(getTextSelectionStylesSummary(editingTextbox, this.textStyles, this.uid, this.getSerializedItemShadow()));
    const toggledValue = summary[toggledProperty] !== true;
    const runStylesPartial: Partial<TextRunStylesObject> = {};
    runStylesPartial[toggledProperty] = toggledValue;
    if (editingTextbox.selectionStart < editingTextbox.selectionEnd) {
      void this.applyStylesToTextSelection(runStylesPartial);
    } else {
      this.setPendingTypingStyles(runStylesPartial);
    }
  }

  public updateActiveText(text = ''): void {
    this.replaceWholeTextPreservingRuns(text);
    void this.updateText(text);
  }

  /**
   * Shifts the live textbox's styles for a wholesale text replacement (a plain-textarea surface
   * handing over the full new string) by replacing only the diffed grapheme range through
   * fabric's editing API — runs and per-line records then survive exactly as typed edits would.
   */
  private replaceWholeTextPreservingRuns(text: string): void {
    if (text !== this.text) {
      replaceTextboxTextPreservingStyles(this.fabricTextbox, text);
    }
  }

  /**
   * Updates the text while growing the text box so the content never soft-wraps: line breaks
   * happen only where the user typed a newline. Used by the mobile text panel's textarea, which
   * (unlike on-canvas editing) should keep each typed line on a single line. The box only ever
   * grows — it is never shrunk below its current width, so wrapping is prevented without the box
   * collapsing when text is removed.
   */
  public async updateTextWithoutWrapping(textItemObject: DeepPartial<TextItemObject>, opts: UpdateFromObjectOpts = {}): Promise<void> {
    const isReplacingText = textItemObject.text !== undefined && textItemObject.text !== this.text;
    if (isReplacingText && textItemObject.text !== undefined) {
      this.replaceWholeTextPreservingRuns(textItemObject.text);
    }
    await this.updateFromObject(
      {
        ...(isReplacingText ? {styleRuns: harvestStyleRunsFromFabricTextbox(this.fabricTextbox), lineStyles: harvestLineStylesFromFabricTextbox(this.fabricTextbox)} : {}),
        ...textItemObject,
        baseWidth: this.getGrownBaseWidthForText(textItemObject.text ?? this.text),
      },
      opts
    );
  }

  /**
   * Returns the base width needed to keep `text` on a single line per hard newline, never below the
   * current width (grow-only, so the box never collapses when text is removed). This is the fit used
   * by the text panel / slide HTML editors so typing never soft-wraps a word.
   */
  public getGrownBaseWidthForText(text: string): number {
    return Math.max(this.baseWidth, this.getWidthToFitUnwrappedText(text));
  }

  /**
   * Measures the width the text box needs so the given text renders without any soft-wrapping,
   * i.e. the width of the widest hard line (split only on explicit newlines). The live fabric
   * textbox is measured with wrapping effectively disabled, then its state is restored.
   */
  private getWidthToFitUnwrappedText(text: string): number {
    const textbox = this.fabricTextbox;
    const originalText = textbox.text;
    const originalWidth = textbox.width;
    textbox.set({text, width: UNWRAPPED_TEXT_MEASURE_WIDTH});
    textbox.initDimensions();
    const fittedWidth = textbox.calcTextWidth() + this.textStyles.fontSize * UNWRAPPED_TEXT_WIDTH_BUFFER_FACTOR;
    textbox.set({text: originalText, width: originalWidth});
    textbox.initDimensions();
    return fittedWidth;
  }

  public getUpdatedModelCoords(textChildCenterPoint: FabricPoint, groupBottomLeft: Point): ModelCoordinates {
    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        return this.getCoordsForTopVerticalAlign();

      case TextVerticalAlignType.CENTER:
        return this.getCoordsForCenterVerticalAlign(textChildCenterPoint);

      case TextVerticalAlignType.BOTTOM:
        return this.getCoordsForBottomVerticalAlign(groupBottomLeft);

      default:
        throw new Error(`Invalid text align type`);
    }
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically top aligned.
   */
  protected getCoordsForTopVerticalAlign(): ModelCoordinates {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? this.fabricObject.group : this.fabricObject;

    if (parentViewComponent) {
      const centerPoint = parentViewComponent.getCenterPoint();
      const theta = util.degreesToRadians(parentViewComponent.angle ?? 0);

      return {
        x:
          centerPoint.x -
          ((this.fabricObject.width * parentViewComponent.scaleX) / 2) * Math.cos(theta) +
          ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.sin(theta),
        y:
          centerPoint.y -
          ((this.fabricObject.width * parentViewComponent.scaleY) / 2) * Math.sin(theta) -
          ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.cos(theta),
      };
    }
    return {};
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically center aligned.
   */
  protected getCoordsForCenterVerticalAlign(textChildCenterPoint: FabricPoint): Point {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const vcWidth = this.fabricObject.width;
    const theta = util.degreesToRadians(parentViewComponent.angle);

    return {
      x:
        textChildCenterPoint.x -
        ((vcWidth * parentViewComponent.scaleX) / 2) * Math.cos(theta) +
        ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.sin(theta),
      y:
        textChildCenterPoint.y -
        ((vcWidth * parentViewComponent.scaleY) / 2) * Math.sin(theta) -
        ((this.fabricObject.height * parentViewComponent.scaleY) / 2) * Math.cos(theta),
    };
  }

  /**
   * Returns the coordinates to be used to update model when the text is vertically bottom aligned.
   * @param {Object} blCoords. The bottom left coordinate of group fabricObject before it's dimensions were updated.
   * @return {Object}
   * @private
   */
  protected getCoordsForBottomVerticalAlign(blCoords: Point): Point {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const theta = degreesToRadians(parentViewComponent.angle);

    return {
      x: blCoords.x + this.fabricObject.height * parentViewComponent.scaleX * Math.sin(theta),
      y: blCoords.y - this.fabricObject.height * parentViewComponent.scaleY * Math.cos(theta),
    };
  }

  protected updateGroupDimensions(textChildDimensions: FabricItemDimensions, newVerticalPadding: number): void {
    this.fabricObject.set({
      height: textChildDimensions.height + this.padding * 2 + newVerticalPadding,
      width: textChildDimensions.width + this.padding * 2,
    });
  }

  public override doesfabricObjBelongtoItem(fabricObj: FabricObject | Group): boolean {
    return this.fabricObject === fabricObj || this.fabricTextbox === fabricObj;
  }

  protected onTextEditModeEntered(): void {
    const hiddenTextarea = this.fabricTextbox.hiddenTextarea;
    if (hiddenTextarea) {
      hiddenTextarea.setAttribute('maxlength', MAX_CHARACTERS_LIMIT);
      this.page.spellCheck.checkSpell(this.fabricTextbox, true).catch(noop);
    }
  }

  protected onEditingExited(): void {
    // Commit any debounced typing while the shifted styles map is still current; the values are
    // captured synchronously, so the teardown below is safe to continue.
    void this.flushPendingTextCommit();
    for (const disposeSessionListener of this.editSessionListenerDisposers) {
      disposeSessionListener();
    }
    this.editSessionListenerDisposers = [];
    // Fabric already removed the session's hidden textarea (and with it these listeners) from
    // the DOM before firing editing:exited; only the references need clearing.
    this.hiddenTextareaPasteListener = null;
    this.hiddenTextareaInputListener = null;
    this.hiddenTextareaKeydownListener = null;
    this.hiddenTextareaBeforeInputListener = null;
    this.pendingTypingStyles.discard();
    this.fabricObject.set({subTargetCheck: false, interactive: false});
    this.fabricTextbox.set({perPixelTargetFind: true});
    this.stopKeepingGroupCacheFreshAfterEditSession();
    this.editSessionBaseWidth = null;
    this.selectionFontSizeScaleBaseline = null;
    this.page.poster.redux.clearTextSelectionStyles();
    this.page.spellCheck.closeSuggestionPopUp();
    closeTextSelectionPopup();
    this.page.canvasPanOnTextEdit.afterTextEditExited();

    unhideCustomControls(this.page.hashedID);
    this.#reshapeAfterEditSession();
  }

  public restoreDefaultState(): void {
    if (this.getEditingTextbox()) {
      this.fabricTextbox.exitEditing();
      this.page.activeSelection.setActiveObject(this.fabricObject as unknown as FabricObject);
    }
  }

  /**
   * Gets the canvas-space position of a text-child-shaped object (e.g. an animation clip path)
   * according to the item's vertical alignment.
   */
  public getTextChildAbsolutePosition(textChildViewComponent: FabricObject): ClonePositionalParams {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const cornerPoints = textChildViewComponent.getCornerPoints(parentViewComponent.getCenterPoint());
    let verticalParams: ClonePositionalParams;

    if (parentViewComponent) {
      parentViewComponent.setCoords();
    }

    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        verticalParams = this.#getPaddingInsetPositionFromCorner(parentViewComponent.aCoords.tl, parentViewComponent, 1);
        break;

      case TextVerticalAlignType.CENTER:
        verticalParams = {
          top: (cornerPoints.tl.y + cornerPoints.bl.y) / 2,
          left: (cornerPoints.tl.x + cornerPoints.bl.x) / 2,
        };
        break;

      case TextVerticalAlignType.BOTTOM:
        verticalParams = this.#getPaddingInsetPositionFromCorner(parentViewComponent.aCoords.bl, parentViewComponent, -1);
        break;

      default:
        throw new Error(`Invalid text align type`);
    }

    verticalParams.originY = this.verticalAlign;
    return verticalParams;
  }

  /**
   * Canvas-space position of the text child inset by the item padding from a group corner: the
   * inset vector (padding·scaleX, verticalDirection·padding·scaleY) rotated by the group's angle.
   */
  #getPaddingInsetPositionFromCorner(corner: FabricPoint, parentViewComponent: FabricObject | Group, verticalDirection: 1 | -1): ClonePositionalParams {
    const theta = degreesToRadians(parentViewComponent.angle);
    const insetX = this.padding * parentViewComponent.scaleX;
    const insetY = verticalDirection * this.padding * parentViewComponent.scaleY;

    return {
      left: corner.x + insetX * Math.cos(theta) - insetY * Math.sin(theta),
      top: corner.y + insetX * Math.sin(theta) + insetY * Math.cos(theta),
    };
  }

  public getPaddedTextChildAbsolutePosition(textChildViewComponent: FabricObject): ClonePositionalParams {
    const parentViewComponent = this.hasParentGroupGraphicItem() ? (this.fabricObject.group ?? this.fabricObject) : this.fabricObject;
    const cornerPoints = textChildViewComponent.getCornerPoints(parentViewComponent.getCenterPoint());
    let verticalParams: ClonePositionalParams;

    if (parentViewComponent) {
      parentViewComponent.setCoords();
    }

    switch (this.verticalAlign) {
      case TextVerticalAlignType.TOP:
        verticalParams = {
          top: parentViewComponent.aCoords.tl.y,
          left: parentViewComponent.aCoords.tl.x,
        };
        break;

      case TextVerticalAlignType.CENTER:
        verticalParams = {
          top: (cornerPoints.tl.y + cornerPoints.bl.y) / 2,
          left: (cornerPoints.tl.x + cornerPoints.bl.x) / 2,
        };
        break;

      case TextVerticalAlignType.BOTTOM:
        verticalParams = {
          top: parentViewComponent.aCoords.bl.y,
          left: parentViewComponent.aCoords.bl.x,
        };
        break;

      default:
        throw new Error(`Invalid text align type`);
    }

    verticalParams.originY = this.verticalAlign;
    return verticalParams;
  }

  public getMinWidth(): number {
    if (this.#isShapedBoxMaster()) {
      return this.#getShapedFlooredSize().width;
    }
    return this.fabricTextbox.getMinWidth() + this.fabricTextbox.strokeWidth + this.padding * 2;
  }

  /**
   * The smallest box a side handle may drag the shaped text into. A figure's is the box its text
   * takes at its font (see {@link #getOutlineShapeSmallestBox}): a side handle takes the letters'
   * spread away and stops, never the font — the corner handles scale that (owner, 2026-10-29).
   * Any other shape's is its layout at the font floor, scaled down from the current layout (the
   * fit is linear but for the fixed margins). A handle drag stops here, so the layout never has to
   * grow the box back under the pointer, which read as the item being dragged.
   */
  #getShapedFlooredSize(): {width: number; height: number} {
    const textbox = this.fabricTextbox;
    if (this.shape.isOutlineShape()) {
      return this.#getOutlineShapeSmallestBox(textbox);
    }
    const floorFactor = Math.min(1, SHAPED_FIT_FONT_SIZE_MIN / Math.max(this.textStyles.fontSize, SHAPED_FIT_FONT_SIZE_MIN));
    return this.#scaleShapedBox(textbox, floorFactor);
  }

  /**
   * A figure's smallest box at its font: the figure its text alone needs (or the figure's own
   * minimum), the glyphs' reach round it as the layout measures it, the margins, the list gutter
   * and the inset. A figure the box had opened out shrinks back down to this and no further: a
   * box dragged in past it grew straight back from its top-left under the pointer, and a top or
   * left handle walked the item instead of resizing it (2026-10-29).
   */
  #getOutlineShapeSmallestBox(textbox: Textbox): {width: number; height: number} {
    const fixedMargin = this.#getShapedFixedMargin(textbox);
    const inset = this.getBoxInset();
    const figure = getTextShapeOutline({shape: this.shape.toObject(), textLength: this.#getShapedPlainTextLength(textbox), fontSize: textbox.fontSize, letterCount: this.#getShapedLetterCount(textbox)});
    const reach = this.#getMeasuredGlyphReachRoundFigure(textbox);
    return {
      width: (figure?.width ?? 0) + reach.width + fixedMargin + this.#getShapedListGutter(textbox) + inset,
      height: (figure?.height ?? 0) + reach.height + fixedMargin + inset,
    };
  }

  public onModifyWidth(): void {
    if (this.#isShapedBoxMaster()) {
      this.#fitShapedTextToBox();
    } else {
      this.updateTextChildWidth();
      this.updateFabricGroupHeight();
    }
    this.setTextChildPosition();
    this.setBackgroundParams();
  }

  public updateTextChildWidth(): void {
    this.fabricTextbox.set({
      width: this.calculateBaseWidth(),
    });
  }

  public calculateBaseWidth(): number {
    return this.fabricObject.width - this.padding * 2 - this.getStrokeWidth();
  }

  protected calculateGroupWidth(): number {
    return this.getTextStrokedWidth() + this.padding * 2;
  }

  protected calculateGroupHeight(): number {
    return this.getTextStrokedHeight() + this.padding * 2 + this.#getVerticalPaddingInBox();
  }

  /**
   * The room below the text the user gave the box, as the box carries it: none while the box is
   * its path's own (a spiral, a drawn path), which encloses the path and its glyphs exactly and
   * offers no handle to take the room away again. The model keeps the room for the plain box
   * the shape returns to.
   */
  #getVerticalPaddingInBox(): number {
    return this.shape.isBoxSizedByPath() ? 0 : this.verticalPadding;
  }

  /**
   * The stroke the laid-out textbox carries, which the group width adds and the base width
   * subtracts; both must use the same figure or every write-back of the measurements (a resize)
   * grows the box by the difference. Before the textbox exists the stroke is derived the way
   * {@link applyTextStroke} will apply it: an enabled outline wins over the faux-bold stroke.
   */
  protected getStrokeWidth(): number {
    if (this.fabricTextbox) {
      return this.fabricTextbox.strokeWidth;
    }
    if (this.textStyles.stroke) {
      return this.getOutlineStrokeWidth();
    }
    if (this.textStyles.isBold && !isBoldVariationAvaliableForFont(this.textStyles.fontFamily)) {
      return getBoldStrokeWidth(this);
    }
    return DEFAULT_STROKE_WIDTH;
  }

  protected getOutlineStrokeWidth(): number {
    return TEXT_OUTLINE_STROKE_WIDTH_FACTOR * this.textStyles.fontSize * this.textStyles.strokeWidth;
  }

  public override getManualSelectorPadding(): number {
    return this.padding * this.fabricObject.scaleX;
  }

  protected updateFabricGroupHeight(): void {
    this.fabricObject.height = this.calculateGroupHeight();
  }

  protected updateFabricGroupWidth(): void {
    this.fabricObject.width = this.calculateGroupWidth();
  }

  protected getTextStrokedWidth(): number {
    return this.fabricTextbox.width + this.fabricTextbox.strokeWidth;
  }

  public getTextStrokedHeight(): number {
    return this.fabricTextbox.height + this.fabricTextbox.strokeWidth;
  }

  public setTextChildPosition(): void {
    this.setTextTopPositionByVerticalAlignment();
    this.setTextLeftPosition();
  }

  protected setTextTopPositionByVerticalAlignment(): void {
    this.fabricTextbox.set({top: this.#getTextChildTop(this.getTextStrokedHeight())});
  }

  /**
   * Where a text child of this stroked height sits in the box: by the vertical alignment, or in the
   * middle of the box through a shaped edit session, whatever the shape (owner, 2026-10-29: the
   * middle of a ring, a square, a heart, a spiral or an arch is clear of its glyphs, and a typing
   * box that sat at the top edge on a curve but in the middle on a circle read as inconsistent).
   */
  #getTextChildTop(childStrokedHeight: number): number {
    const spareHeight = this.fabricObject.height - this.padding * 2 - childStrokedHeight;
    const slackAbove = this.shapedEditSession !== null ? spareHeight / 2 : this.#getVerticalSlackOffset(spareHeight);
    return -this.fabricObject.height / 2 + this.padding + slackAbove;
  }

  protected setTextLeftPosition(): void {
    const slackOffset = this.#isShapedBoxMaster() && !this.getEditingTextbox() ? this.#getShapedHorizontalSlackOffset(this.fabricTextbox.width) : 0;
    this.fabricTextbox.set({
      left: -this.fabricObject.width / 2 + this.padding + slackOffset,
    });
  }

  /** The vertical alignment the text child sits by: the item's own, except that a wave always sits centred in a box it is fitted into. */
  #getVerticalAlignInBox(): TextVerticalAlignType {
    return this.#isAlwaysCentredInShapedBox() ? TextVerticalAlignType.CENTER : this.verticalAlign;
  }

  #isAlwaysCentredInShapedBox(): boolean {
    return this.#isShapedBoxMaster() && this.shape.isAlwaysCentredInBox();
  }

  /** A fitted glyph box of this width sits in the master box's spare width by the text's alignment (left, centred, or right), or centred for a wave. */
  #getShapedHorizontalSlackOffset(childWidth: number): number {
    return this.#getSlackOffsetByAlignment(Math.max(0, this.#getShapedBoxInnerSize().width - childWidth));
  }

  /** How much of a box's spare height sits above a child by the vertical alignment. */
  #getVerticalSlackOffset(slack: number): number {
    return getOffsetByVerticalAlign(this.#getVerticalAlignInBox(), slack);
  }

  /** How much of a box's spare width sits left of a child by the text's alignment, or half for a shape that always sits centred. */
  #getSlackOffsetByAlignment(slack: number): number {
    if (this.#isAlwaysCentredInShapedBox()) {
      return slack / 2;
    }
    switch (this.textStyles.textAlign) {
      case TextHorizontalAlignType.LEFT:
        return 0;
      case TextHorizontalAlignType.CENTER:
      case TextHorizontalAlignType.JUSTIFY:
        return slack / 2;
      case TextHorizontalAlignType.RIGHT:
        return slack;
      default:
        return assertNever(this.textStyles.textAlign);
    }
  }

  public getMinHeight(): number {
    if (this.#isShapedBoxMaster()) {
      return this.#getShapedFlooredSize().height;
    }
    return this.getTextStrokedHeight() + this.padding * 2;
  }

  public onModifyHeight(): void {
    this.#fitShapedTextToBox();
    this.setTextChildPosition();
    this.setBackgroundParams();
  }

  public getVerticalPadding(): number {
    if (this.shape.isBoxSizedByPath()) {
      return this.verticalPadding;
    }
    return this.fabricObject.height - this.getTextStrokedHeight() - this.padding * 2;
  }

  public getFill(): string | TFiller | null {
    return this.fabricTextbox.fill;
  }

  public override hasEditMode(): boolean {
    return true;
  }

  public override getColors(): RGBA[] {
    let colors: RGBA[] = super.getColors();

    if (this.textStyles.fill.hasFill()) {
      colors = [...colors, ...this.textStyles.fill.getFillColor()];
    }

    if (this.hasBackground()) {
      colors = [...colors, ...this.background.fill.getFillColor()];
    }

    for (const lineStyleObject of Object.values(this.lineStyles)) {
      if (lineStyleObject.list) {
        if (lineStyleObject.list.fill !== null) {
          colors.push(lineStyleObject.list.fill);
        }
      }
    }

    return [...colors, ...this.styleRuns.getColors()];
  }
}

export const addTextToPoster = (coordinates?: ItemCoordinates): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  void currentPage.items.addItems.addTextItem({
    type: ElementDataType.TEXT,
    ...coordinates,
  });
};


/**
 * FONTSIZE_FRACTION is a hash map where the key is the font family, this is used to assign a value to _fontSizeFraction
 * when the poster made in flash is opened in HTML5 builder.
 */
export const FONTSIZE_FRACTION = {
  AccidentalPresidency: 0.18,
  Acknowledgement: 0.3,
  AdventProRegular: 0.15,
  AgentOrange: 0.285,
  AlexBrushRegular: 0.285,
  AnimeAce: 0.27,
  AnuDaw: 0.235,
  ArbuckleRemix: 0.38,
  ArchivoBlackRegular: 0.39,
  AsapRegular: 0.18,
  AYummyApology: 0.39,
  Bangers: 0.22,
  BerkshireSwashRegular: 0.12,
  BigApple: 0.31,
  Blackout2am: 0.35,
  BlackoutMidnight: 0.28,
  BlackRose: 0.21,
  BostonTraffic: 0.29,
  BowlbyOneSCRegular: 0.04,
  BubbleBoy2: 0,
  BurnstownDam: 0.42,
  CACChampagne: 0.34,
  Caeldera: 0.39,
  Chancery: 0,
  Chopin: 0,
  Chumbly: 0.45,
  Coda: 0.28,
  ComingSoon: 0.35,
  Concielian: 0.42,
  DayPosterBlack: 0.02,
  DigitalDream: 0.34,
  Effloresce: 0.29,
  EraserRegular: 0.15,
  FingerPaintRegular: 0.02,
  FrijoleRegular: 0.1,
  FugazOneRegular: 0.08,
  GermaniaOneRegular: 0.22,
  HamburgerHeaven: 0.18,
  HennyPennyRegular: 0.0,
  HoltwoodOneSC: -0.08,
  JockeyOneRegular: 0.05,
  JosefinSlabRegular: 0.39,
  KaushanScriptRegular: 0.03,
  LeagueSpartanBold: 0.38,
  LemonRegular: 0.09,
  LiberationSerif: 0.42,
  Liquidism: 0.34,
  LoveYaLikeASister: 0.2,
  MerriweatherSansRegular: 0.15,
  MetalLord: 0.38,
  MetalManiaRegular: 0.19,
  Monoton: -0.05,
  MountainsofChristmas: 0.2,
  MouseMemoirsRegular: 0.18,
  NixieOneRegular: 0.18,
  Oklahoma: 0.1,
  OldStandardRegular: 0.36,
  OpenSansRegular: 0.36,
  OperatingInstructions: 0.36,
  OriginalSurferRegular: 0.14,
  PaprikaRegular: -0.03,
  PompiereRegular: 0.19,
  Porter: 0.38,
  Primitive: 0.2,
  SacramentoRegular: 0.2,
  SancreekRegular: 0.09,
  SevenMonkeyFury: 0.23,
  SFCollegiate: 0.34,
  SFSpeedwaystar: 0.45,
  ShadowsIntoLightTwoRegular: 0.02,
  Shermlock: 0.065,
  SirinStencilRegular: -0.05,
  SixCaps: 0.025,
  SmokumRegular: 0.14,
  SoftSugar: 0.325,
  Solov2: 0.42,
  SpecialElite: 0.42,
  SpiraxRegular: 0.19,
  Sveningsson: 0.19,
  TagsXtreme: 0.36,
  Titania: 0.22,
  TulpenOneRegular: 0.25,
  Ultra: 0.12,
  WaitingfortheSunrise: 0.0,
  Windsong: 0.32,
};

/** How much of a box's spare height sits above a child by the vertical alignment: none, half, or all of it. */
const getOffsetByVerticalAlign = (verticalAlign: TextVerticalAlignType, slack: number): number => {
  switch (verticalAlign) {
    case TextVerticalAlignType.TOP:
      return 0;
    case TextVerticalAlignType.CENTER:
      return slack / 2;
    case TextVerticalAlignType.BOTTOM:
      return slack;
    default:
      return assertNever(verticalAlign);
  }
};

/** A shaped item lays everything out on one line, so hard line breaks become spaces (a newline and a space are both one grapheme, so every run and selection index stays valid). */
const joinLinesForShapedLayout = (text: string): string => {
  return text.replace(/\n/g, ' ');
};
